import { groupBy, kebabCase } from "lodash-es";
import qs from "query-string";
import invariant from "tiny-invariant";

import CumulAccess from "@mapmycustomers/shared/enum/cumul/CumulAccess";
import CumulShareType from "@mapmycustomers/shared/enum/cumul/CumulShareType";
import EmailTone from "@mapmycustomers/shared/enum/email/EmailTone";
import EmailFolder from "@mapmycustomers/shared/enum/EmailFolder";
import EmailStatus from "@mapmycustomers/shared/enum/EmailStatus";
import type LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import NylasType from "@mapmycustomers/shared/enum/NylasType";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import type Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import type AnyColor from "@mapmycustomers/shared/types/AnyColor";
import type Identified from "@mapmycustomers/shared/types/base/Identified";
import type Located from "@mapmycustomers/shared/types/base/Located";
import type { GeocodeLimits, GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import {
  FieldAgentApplyRequest,
  FieldAgentApplyResponse,
  FieldAgentChatRequest,
  FieldAgentChatResponse,
} from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import type { FieldAgentTranscribeResponse } from "@mapmycustomers/shared/types/chat/FieldAgentTranscribe";
import type CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import type Currency from "@mapmycustomers/shared/types/Currency";
import type CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import type { CustomFieldPayload } from "@mapmycustomers/shared/types/customField/CustomField";
import CustomFieldValidateResponse from "@mapmycustomers/shared/types/customField/CustomFieldValidateResponse";
import type CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import CustomFieldValuesUpsertPayload from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertPayload";
import CustomFieldValuesUpsertResponse from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";
import Draft from "@mapmycustomers/shared/types/draft/Draft";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailEntity from "@mapmycustomers/shared/types/email/EmailEntity";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailRecipientAddress from "@mapmycustomers/shared/types/email/EmailRecipientAddress";
import EmailSubscriptionStatus from "@mapmycustomers/shared/types/email/EmailSubscriptionStatus";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import EmailUnsubscribeInfo from "@mapmycustomers/shared/types/email/EmailUnsubscribeInfo";
import EntityEmailSync from "@mapmycustomers/shared/types/email/EntityEmailSync";
import EntityEmailSyncDismissPayload from "@mapmycustomers/shared/types/email/EntityEmailSyncDismissPayload";
import GenerateEmailResponse from "@mapmycustomers/shared/types/email/GenerateEmailResponse";
import {
  Activity,
  AnyEntity,
  AnyEntityId,
  Deal,
  EntitiesSupportedByEmailFeature,
  EntitiesSupportingFiles,
  EntitiesSupportingHistory,
  EntitySupportingCustomFields,
  EntitySupportingRecordRestoration,
  EntityType,
  EntityTypesSupportedByEmailFeature,
  EntityTypesSupportedByImport,
  EntityTypesSupportedByIntegrations,
  EntityTypesSupportingBoundaryLegends,
  EntityTypesSupportingFieldCustomization,
  EntityTypesSupportingRestoration,
  EntityTypesSupportingUniqueStandardFields,
  EntityTypeSupportingCustomFields,
  EntityTypeSupportingDataMerging,
  EntityTypeSupportingFiles,
  EntityTypeSupportingGroups,
  EntityTypeSupportingHistory,
  EntityTypeSupportingNotes,
  Group,
  Guide,
  GuideUserStatus,
  Lead,
  MapEntity,
  Person,
} from "@mapmycustomers/shared/types/entity";
import type ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import type { ActivityOrSuggestion } from "@mapmycustomers/shared/types/entity/ActivitySuggestion";
import {
  BusinessRuleAssignmentPayload,
  BusinessRuleCreatePayload,
  BusinessRuleUpdatePayload,
  PlatformBusinessRule,
} from "@mapmycustomers/shared/types/entity/BusinessRule";
import type Note from "@mapmycustomers/shared/types/entity/common/Note";
import type Company from "@mapmycustomers/shared/types/entity/Company";
import CompanyWithLeadId from "@mapmycustomers/shared/types/entity/CompanyWithLeadId";
import type CreateDealLossReasonPayload from "@mapmycustomers/shared/types/entity/deals/CreateDealLossReasonPayload";
import type DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import type Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import type Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import type { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import type DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import type {
  DuplicateEntity,
  DuplicatePair,
  DuplicateRun,
} from "@mapmycustomers/shared/types/entity/Duplicate";
import type EntityHistoryRow from "@mapmycustomers/shared/types/entity/EntityHistoryRow";
import type {
  ExecutiveSummarySchedule,
  ExecutiveSummaryScheduleCreatePayload,
  ExecutiveSummaryScheduleListResponse,
  ExecutiveSummaryScheduleUpdatePayload,
} from "@mapmycustomers/shared/types/entity/executiveSummarySchedule";
import type {
  FiCatalogResponse,
  FiChartHiddenState,
  FiChartSettings,
  FiChartSummary,
  FiChartSummaryPayload,
  FiExecuteRequest,
  FiExecuteResponse,
  FiFocusArea,
  FiLimits,
  FiPresetApplyMode,
  FiPresetApplyResult,
  FiPresetRoleKey,
  FiSummarizeRequest,
  FiSummarizeResponse,
  FiWeeklySummaryPreset,
  FiWeeklySummaryPresetApplyResult,
  FiWeeklySummaryPresetListResponse,
  FiWeeklySummaryPresetPayload,
  FiWeeklySummaryRequest,
  FiWeeklySummaryResolvedSettings,
  FiWeeklySummaryResponse,
  FiWeeklySummarySettings,
  FiWeeklySummarySettingsListResponse,
  FiWeeklySummarySettingsPayload,
  FiWordCloudPreset,
  FiWordCloudPresetListResponse,
  FiWordCloudPresetPayload,
  FiWordCloudRequest,
  FiWordCloudResolvedSettings,
  FiWordCloudResponse,
  FiWordCloudSettings,
  FiWordCloudSettingsListResponse,
  FiWordCloudSettingsPayload,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import type LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import PersonWithLeadId from "@mapmycustomers/shared/types/entity/PersonWithLeadId";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import Route, {
  CreateRoutePayload,
  PreviewRoutePayload,
  RoutePreview,
  RoutePreviewMetrics,
  RouteStop,
  RouteStopBulkItem,
  TransferStopsPayload,
  TransferStopsResponse,
} from "@mapmycustomers/shared/types/entity/Route";
import type Territory from "@mapmycustomers/shared/types/entity/Territory";
import type { TerritoryDetail } from "@mapmycustomers/shared/types/entity/Territory";
import Trip, {
  CreateTripPayload,
  UpdateTripPayload,
} from "@mapmycustomers/shared/types/entity/Trip";
import UpdateCompanyPayload from "@mapmycustomers/shared/types/entity/UpdateCompanyPayload";
import UpdatePersonPayload from "@mapmycustomers/shared/types/entity/UpdatePersonPayload";
import UpdateRoutePayload from "@mapmycustomers/shared/types/entity/UpdateRoutePayload";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import type RawFile from "@mapmycustomers/shared/types/File";
import { default as MmcFile } from "@mapmycustomers/shared/types/File";
import type Import from "@mapmycustomers/shared/types/imports/Import";
import type ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import type ImportPayload from "@mapmycustomers/shared/types/imports/ImportPayload";
import type IntegrationRaw from "@mapmycustomers/shared/types/integrations/IntegrationRaw";
import FormLayout, { ChildLayout } from "@mapmycustomers/shared/types/layout/FormLayout";
import FormLayoutRoleAssociation, {
  FormLayoutRoleAssociationRecord,
} from "@mapmycustomers/shared/types/layout/FormLayoutRoleAssociation";
import type Location from "@mapmycustomers/shared/types/Location";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import BoundaryLegend, {
  CreateBoundaryLegendPayload,
  UpdateBoundaryLegendPayload,
} from "@mapmycustomers/shared/types/map/BoundaryLegend";
import BoundaryStatsResponse from "@mapmycustomers/shared/types/map/BoundaryStatsResponse";
import type PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import type {
  CreateColorPinLegendPayload,
  CreateShapePinLegendPayload,
  UpdateColorPinLegendPayload,
  UpdateShapePinLegendPayload,
} from "@mapmycustomers/shared/types/map/PinLegend";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import type Organization from "@mapmycustomers/shared/types/Organization";
import Permission from "@mapmycustomers/shared/types/Permission";
import type Plan from "@mapmycustomers/shared/types/Plan";
import { ResourceResponse } from "@mapmycustomers/shared/types/resources/ResourceResponse";
import type StorageConnection from "@mapmycustomers/shared/types/resources/StorageConnection";
import type Role from "@mapmycustomers/shared/types/Role";
import {
  OrgCloneRow,
  OrgCloneRun,
  SandboxAccessTokenData,
  SandboxConfig,
} from "@mapmycustomers/shared/types/Sandbox";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import type Setting from "@mapmycustomers/shared/types/Setting";
import type { GeoMultiPolygon, GeoPolygon } from "@mapmycustomers/shared/types/shapes";
import type GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import CustomMetric from "@mapmycustomers/shared/types/stackRank/CustomMetric";
import type Team from "@mapmycustomers/shared/types/Team";
import { TokenHandoffCodeData } from "@mapmycustomers/shared/types/TokenHandoff";
import type User from "@mapmycustomers/shared/types/User";
import type { UserRef } from "@mapmycustomers/shared/types/User";
import UserLogin from "@mapmycustomers/shared/types/userLogin/UserLogin";
import type ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import type { SimpleListResponse } from "@mapmycustomers/shared/types/viewModel/ListResponse";
import type PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import type {
  PinLegendsFilter,
  PlatformSimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import type PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";
import type PlatformMapRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformMapRequest";
import type SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import type { PlatformSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { ValidationError } from "@mapmycustomers/shared/util/api/ApiError";
import { geometryFromBinary, geometryToBinary } from "@mapmycustomers/shared/util/territory/binary";
import type { Nullable, OnlyRequiredFields, OptionalFields } from "@mapmycustomers/shared/util/ts";

import type { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import UserRequestStatus from "@app/enum/sso/UserRequestStatus";
import { FieldAccessUpdate } from "@app/scene/settings/component/ManageFields/type/FieldAccessUpdate";
import { convertFromRawDashboard, convertToRawDashboard } from "@app/store/api/utils/dashboards";
import { addExtraCoordinate, removeExtraCoordinate } from "@app/store/api/utils/territories";
import authService from "@app/store/auth/AuthService";
import AiFeedbackPayload from "@app/types/AiFeedbackPayload";
import type InvitationInfo from "@app/types/auth/InvitationInfo";
import type LoginAppRollout from "@app/types/auth/LoginAppRollout";
import type TokenResponse from "@app/types/auth/TokenResponse";
import UserDetails from "@app/types/auth/UserDetails";
import CloneCustomFieldPayload from "@app/types/CloneCustomFieldPayload";
import Dashboard from "@app/types/dashboard/Dashboard";
import Scope from "@app/types/dashboard/Scope";
import { RawTimelineEvent } from "@app/types/events/TimelineEvent";
import type FaceTime from "@app/types/FaceTime";
import type FieldAgentMessageFeedbackPayload from "@app/types/FieldAgentMessageFeedbackPayload";
import type FilePreview from "@app/types/FilePreview";
import type Iam from "@app/types/Iam";
import type CreateIntegrationPayload from "@app/types/integrations/CreateIntegrationPayload";
import type Integration from "@app/types/integrations/Integration";
import type IntegrationField from "@app/types/integrations/IntegrationField";
import type IntegrationFieldResponse from "@app/types/integrations/IntegrationFieldResponse";
import type IntegrationSync from "@app/types/integrations/IntegrationSync";
import type IntegrationUser from "@app/types/integrations/IntegrationUser";
import type PlatformIntegrationField from "@app/types/integrations/PlatformIntegrationField";
import type ReauthenticateIntegrationPayload from "@app/types/integrations/ReauthenticateIntegrationPayload";
import type SalesforceAuthUrl from "@app/types/integrations/SalesforceAuthUrl";
import DemographicData, { DemographicMetadata } from "@app/types/map/DemographicData";
import LeadFinderLimit from "@app/types/map/LeadFinderLimit";
import type MmcNotification from "@app/types/MmcNotification";
import type CalendarSyncAffectedUser from "@app/types/nylas/CalendarSyncAffectedUser";
import type NylasFolder from "@app/types/nylas/NylasFolder";
import type { NylasInfoCreatePayload, NylasInfoUpdatePayload } from "@app/types/nylas/NylasInfo";
import NylasInfo, { CreateNylasEventPayload } from "@app/types/nylas/NylasInfo";
import type Report from "@app/types/Report";
import type BrowseResponse from "@app/types/resources/BrowseResponse";
import type {
  CreateConnectionPayload,
  UpdateConnectionPayload,
} from "@app/types/resources/ConnectionPayloads";
import ResourcesCreatePayload from "@app/types/resources/ResourcesCreatePayload";
import type Statistics from "@app/types/Statistics";
import type TextSearchResult from "@app/types/TextSearchResult";
import type AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import type ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import type NotificationListResponse from "@app/types/viewModel/NotificationListResponse";
import audioFileExtension from "@app/util/chat/voice/audioFileExtension";
import { WEB_CLIENT_ID, WEB_TOKEN_SCOPE } from "@app/util/consts";
import loggingService from "@app/util/logging";
import parseDatedFields from "@app/util/parsers/parseDatedFields";

import UserRecent, { UserRecentPayload } from "../../types/userRecent/UserRecent";

import BaseApiService, { RequestOptions } from "./BaseApiService";
import { getFilesEndpoint } from "./utils/files";
// TODO: do not use scene imports, fix all below this comment
import CoachingFile, { CoachingFileUpdatePayload } from "@app/scene/dashboard/types/CoachingFile";
import CoachingNote, {
  CoachingNoteAnswer,
  CoachingNoteVisibility,
} from "@app/scene/dashboard/types/CoachingNote";
import CoachingNoteCategory from "@app/scene/dashboard/types/CoachingNoteCategory";

export type CreateCompanyPayload = Omit<UpdateCompanyPayload, "files"> & {
  files?: MmcFile["id"][];
};

export type CreatePersonPayload = Omit<UpdatePersonPayload, "files"> & {
  files?: MmcFile["id"][];
};

export type UpdateDealPayload = Partial<
  // color is legend-resolved per response, not a stored deal field, so it is not part of an update.
  Omit<
    Deal,
    "account" | "color" | "contact" | "dealLossReason" | "files" | "funnel" | "stage" | "user"
  > & {
    account: null | Pick<Company, "id">;
    contact: null | Pick<Person, "id">;
    dealLossReason: null | Pick<DealLossReason, "id">;
    files?: MmcFile["id"][];
    funnel: Pick<Funnel, "id">;
    stage: Pick<Stage, "id">;
    user: { id: User["id"] };
  }
>;

export type CreateDealPayload = Omit<UpdateDealPayload, "files"> & {
  files?: MmcFile["id"][];
};

export type ActivityPayload = Partial<
  Omit<
    Activity,
    "account" | "assignee" | "contact" | "contacts" | "crmActivityType" | "deal" | "files"
  > & {
    account: null | Pick<Company, "id">;
    assigneeId: User["id"];
    coachingNoteId?: CoachingNote["id"] | null;
    contact: null | Pick<Person, "id">;
    contacts: Pick<Person, "id">[];
    crmActivityType?: Pick<ActivityType, "id">;
    deal: null | Pick<Deal, "id">;
    files?: MmcFile["id"][];
    layoutId?: FormLayout["id"];
    // Authoritative primary designator for multi-contact activities (must be one
    // of `contacts[]`). `contact` is the legacy single-contact designator.
    primaryContactId?: Person["id"];
    subjectUserId?: null | User["id"];
  }
>;

export type ActivityTypePayload = OptionalFields<
  Pick<ActivityType, "active" | "color" | "displayOrder" | "name">,
  "active" | "color"
>;

export interface ActivityCreationPayloadWithCustomFields extends Omit<
  ActivityPayload,
  "customFields"
> {
  customFields?: {
    customFieldData: {
      customField: Pick<CustomField, "id">;
      value: CustomFieldValueType;
    }[];
  }[];
}

export type CreateTerritoryPayload = Pick<Territory, "name" | "postalCodes"> & {
  color?: AnyColor;
  territoryDetail: Pick<
    TerritoryDetail,
    "hidden" | "opacity" | "region" | "requireRuleAssignment" | "type"
  > &
    (
      | Pick<TerritoryDetail, "points">
      | Pick<TerritoryDetail, TerritoryLevel.ADM1 | TerritoryLevel.ADM2 | TerritoryLevel.ADM3>
    );
  user: Pick<User, "id">;
};

export type PreviewEmailPayload = Pick<Email, "body" | "entityType" | "resourceIds" | "subject"> &
  Pick<Partial<Email>, "bcc" | "cc" | "repliedAll" | "replyToMessageId"> & {
    childLayoutId?: ChildLayout["id"];
    entityIds?: EntitiesSupportedByEmailFeature["id"][];
    instant?: boolean;
    isHtml: boolean;
    layoutId?: FormLayout["id"];
  };

export type SendEmailPayload = Pick<
  Email,
  "body" | "entityType" | "logActivity" | "resourceIds" | "schedule" | "subject"
> &
  Pick<Partial<Email>, "bcc" | "cc" | "repliedAll" | "replyToMessageId"> & {
    associatedEntities?: EmailAssociatedEntity[];
    attachments?: RawFile["id"][];
    childLayoutId?: ChildLayout["id"];
    crmActivity?: { name: string } | ActivityCreationPayloadWithCustomFields;
    emailTemplateId?: EmailTemplate["id"];
    entityIds?: EntitiesSupportedByEmailFeature["id"][];
    instant?: boolean;
    isHtml: boolean;
    layoutId?: FormLayout["id"];
    replyAddress?: SendEmailReplyRecipientAddressPayload[];
    tracking: boolean;
  };

export interface SendEmailReplyRecipientAddressPayload extends EmailRecipientAddress {
  entityId?: EntitiesSupportedByEmailFeature["id"];
}

export type CreateEmailTemplatePayload = Pick<
  EmailTemplate,
  "attachmentIds" | "body" | "entityType" | "metadata" | "name" | "resourceIds" | "subject"
>;

export type UpdateEmailTemplatePayload = CreateEmailTemplatePayload & Pick<EmailTemplate, "id">;

export type ResourcePayload = OnlyRequiredFields<Omit<Resource, "folder" | "user">, "id"> & {
  folder?: null | Pick<Folder, "id">;
  user?: Pick<User, "id">;
};

export type FileTagPayload = OnlyRequiredFields<Omit<FileTag, "id">, "name" | "visibility">;

export interface EntityIdWithLayoutId<E extends Identified> {
  id: E["id"];
  layoutId: FormLayout["id"];
}

export type FieldValidationErrors = {
  customField?: Pick<CustomField, "esKey" | "id">;
  error: ValidationError[];
  fieldName?: IField["platformName"];
};

// Platform reports per-record failures of a bulk operation keyed by an entity-specific id field,
// hence all ids are optional here and only the one matching the requested entity type is present.
export type BulkOperationFailedRecord = {
  accountId?: Company["id"];
  activityId?: Activity["id"];
  contactId?: Person["id"];
  crmActivityId?: Activity["id"];
  dealId?: Deal["id"];
  errors: ValidationError[];
};

export type BulkUpdateResponse = [
  successfullyUpdated: AnyEntity[],
  failedToUpdate: BulkOperationFailedRecord[],
];

// platform confirms bulk deletions as bare ids, not as row objects
export type BulkDeleteResponse = [
  successfullyDeletedIds: AnyEntityId[],
  failedToDelete: BulkOperationFailedRecord[],
];

export type CreateEmailDraftPayload = Pick<
  EmailDraft,
  "attachmentIds" | "content" | "metadata" | "title" | "type"
>;
export type CreateDraftPayload = CreateEmailDraftPayload;

export type UpdateEmailDraftPayload = CreateEmailDraftPayload & Pick<EmailDraft, "id">;
export type UpdateDraftPayload = UpdateEmailDraftPayload;

export interface GenerateEmailPayload {
  email?: {
    body: string;
    subject: string;
  };
  entityId?: EntitiesSupportedByEmailFeature["id"];
  entityType?: EntityType;
  prompt: string;
  subjectOnly?: boolean;
  suggestNewAlternative?: boolean;
  tone?: EmailTone;
}

const entityTypeToCustomFieldsEndpoint: { [key in EntityTypeSupportingCustomFields]: string } = {
  [EntityType.ACTIVITY]: "crm-activity-custom-fields",
  [EntityType.COMPANY]: "account-custom-fields",
  [EntityType.DEAL]: "deal-custom-fields",
  [EntityType.PERSON]: "contact-custom-fields",
};

const entityTypeToGroupsEndpoint: { [key in EntityTypeSupportingGroups]: string } = {
  [EntityType.COMPANY]: "account-groups",
  [EntityType.DEAL]: "deal-groups",
  [EntityType.PERSON]: "contact-groups",
} as const;

const upsertCustomFieldPayloadPart: Record<EntityTypeSupportingCustomFields, string> = {
  [EntityType.ACTIVITY]: "crmActivityId",
  [EntityType.COMPANY]: "accountId",
  [EntityType.DEAL]: "dealId",
  [EntityType.PERSON]: "contactId",
} as const;

const entityTypeToDuplicates: { [key in EntityTypeSupportingDataMerging]: string } = {
  [EntityType.COMPANY]: "account-dupes",
  [EntityType.PERSON]: "contact-dupes",
} as const;

const mergeEntity: { [key in EntityTypeSupportingDataMerging]: string } = {
  [EntityType.COMPANY]: "account-dupe-merge",
  [EntityType.PERSON]: "contact-dupe-merge",
} as const;

const entityTypeToDuplicateSearch: { [key in EntityTypeSupportingDataMerging]: string } = {
  [EntityType.COMPANY]: "accounts",
  [EntityType.PERSON]: "contacts",
} as const;

const entityTypeToHistoryEndpointEndpoint: { [key in EntityTypeSupportingHistory]: string } = {
  [EntityType.ACTIVITY]: "crm-activity-history",
  [EntityType.COMPANY]: "account-history",
  [EntityType.DEAL]: "deal-history",
  [EntityType.PERSON]: "contact-history",
} as const;

const entityTypeToEmailUnsubscribeEndpoint: {
  [key in EntityTypesSupportedByEmailFeature]: string;
} = {
  [EntityType.COMPANY]: "account",
  [EntityType.PERSON]: "contact",
} as const;

const entityTypeToEntityEmailSyncEndpoint: {
  [key in EntityTypesSupportedByEmailFeature]: string;
} = {
  [EntityType.COMPANY]: "account",
  [EntityType.PERSON]: "contact",
} as const;

export class ApiService extends BaseApiService {
  protected getAuthToken(): string | undefined {
    return authService.getToken();
  }

  private requestToken = (body: Record<string, string>): Promise<TokenResponse> =>
    this.post("/oauth/token", qs.stringify({ ...body, client_id: WEB_CLIENT_ID }), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

  authorize = ({
    grant_type,
    password,
    refresh_token,
    scope,
    username,
  }: {
    grant_type?: string;
    password?: string;
    refresh_token?: string;
    scope?: string;
    username?: string;
  }): Promise<TokenResponse> =>
    this.requestToken({
      ...(username ? { username } : {}),
      ...(password ? { password } : {}),
      ...(refresh_token ? { refresh_token } : {}),
      grant_type: grant_type ?? "password",
      scope: scope ?? WEB_TOKEN_SCOPE,
    });

  exchangeAuthorizationCode = ({
    code,
    codeVerifier,
    redirectUri,
  }: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
  }): Promise<TokenResponse> =>
    this.requestToken({
      code,
      code_verifier: codeVerifier,
      grant_type: "authorization_code",
      redirect_uri: redirectUri,
    });

  fetchLoginAppRollout = (config?: RequestOptions): Promise<LoginAppRollout> =>
    this.get("/login/rollout", config);

  deleteToken = (
    orgId: Organization["id"],
    userLoginId: UserLogin["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/revoke-tokens/${userLoginId}`, config);

  deleteOtherTokens = (orgId: Organization["id"], config?: RequestOptions): Promise<void> =>
    this.delete(`/organizations/${orgId}/revoke-other-tokens`, config);

  deleteCurrentToken = (orgId: Organization["id"], config?: RequestOptions): Promise<void> =>
    this.delete(`/organizations/${orgId}/revoke-current-token`, config);

  authorizeCumul = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<{ token?: string }> => this.post(`/organizations/${orgId}/cumul/auth`, config);

  authorizeSso = (sessionId: string): Promise<{ accessToken?: string; message?: string }> => {
    return this.get(`/sso/login/${sessionId}`);
  };

  requestAccountSso = (sessionId: string): Promise<{ accessToken?: string; message?: string }> => {
    return this.get(`/sso/request-access/${sessionId}`);
  };

  fetchCumulDashboardList = (
    orgId: Organization["id"],
    payload: {
      cumulSSOKey: string;
      cumulSSOToken: string;
    },
    { $filters, $limit, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<CumulDashboard>> =>
    this.post(
      `/organizations/${orgId}/cumul/dashboards`,
      {
        ...payload,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $order,
      },
      config
    );

  fetchCumulDashboard = (
    orgId: Organization["id"],
    dashboardId: CumulDashboard["id"],
    payload: {
      cumulSSOKey: string;
      cumulSSOToken: string;
    },
    { $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<CumulDashboard> =>
    this.post(
      `/organizations/${orgId}/cumul/dashboards/${dashboardId}`,
      {
        ...payload,
        $order,
      },
      config
    );

  deleteDashboard = (
    orgId: Organization["id"],
    dashboardId: CumulDashboard["id"],
    details: {
      cumulSSOKey: string;
      cumulSSOToken: string;
    },
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/cumul/dashboards/${dashboardId}`, {
      data: details,
      ...config,
    });

  duplicateDashboard = (
    orgId: Organization["id"],
    dashboardId: CumulDashboard["id"],
    details: {
      cumulSSOKey: string;
      cumulSSOToken: string;
      dashboardId: CumulDashboard["id"];
      userId: User["id"][];
    },
    config?: RequestOptions
  ): Promise<CumulDashboard> =>
    this.post(`/organizations/${orgId}/cumul/dashboards/${dashboardId}/clone`, details, config);

  createDashboard = (
    orgId: Organization["id"],
    details: {
      cumulSSOKey: string;
      cumulSSOToken: string;
    },
    config?: RequestOptions
  ): Promise<CumulDashboard> =>
    this.post(`/organizations/${orgId}/cumul/dashboards/create`, details, config);

  shareCumulDashboard = (
    orgId: Organization["id"],
    dashboardId: CumulDashboard["id"],
    payload: {
      [CumulShareType.ORG]?: { access: CumulAccess };
      [CumulShareType.TEAM]?: Array<{ access: CumulAccess; id: Team["id"] }>;
      [CumulShareType.USER]?: Array<{ access: CumulAccess; id: User["id"] }>;
      cumulSSOKey: string;
      cumulSSOToken: string;
      sharedTo: CumulShareType;
    },
    config?: RequestOptions
  ): Promise<void> =>
    this.post(`/organizations/${orgId}/cumul/dashboards/${dashboardId}/share`, payload, config);

  unshareCumulDashboard = (
    orgId: Organization["id"],
    dashboardId: CumulDashboard["id"],
    payload: {
      [CumulShareType.ORG]?: { access: CumulAccess };
      [CumulShareType.TEAM]?: Array<{ access: CumulAccess; id: Team["id"] }>;
      [CumulShareType.USER]?: Array<{ access: CumulAccess; id: User["id"] }>;
      cumulSSOKey: string;
      cumulSSOToken: string;
      sharedTo: CumulShareType;
    },
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/cumul/dashboards/${dashboardId}/unshare`, {
      data: payload,
      ...config,
    });

  fetchMe = (config?: RequestOptions): Promise<Iam> => this.get("/users/me", config);

  fetchRoles = (orgId: Organization["id"], config?: RequestOptions): Promise<ListResponse<Role>> =>
    this.get(`/organizations/${orgId}/roles?$order=priority`, config);

  fetchPermissions = (
    roleId: Role["id"],
    config?: RequestOptions
  ): Promise<ListResponse<Permission>> => this.get(`/roles/${roleId}/permissions`, config);

  fetchTeams = (
    orgId: Organization["id"],
    options: {
      includeDeletedOnly?: boolean;
    } = {},
    config?: RequestOptions
  ): Promise<ListResponse<Team>> => {
    const $filters = { ...options };

    return this.get(`/organizations/${orgId}/teams`, {
      params: {
        $filters: JSON.stringify($filters),
      },
      ...config,
    });
  };

  getUser = (
    orgId: Organization["id"],
    userId: User["id"],
    config?: RequestOptions
  ): Promise<User> => this.get(`/organizations/${orgId}/users/${userId}`, config);

  fetchUsers = (orgId: Organization["id"], config?: RequestOptions): Promise<ListResponse<User>> =>
    this.get(`/organizations/${orgId}/users`, {
      params: { $filters: JSON.stringify({ includeDeleted: true }), $limit: -1 },
      ...config,
    });

  // The only read path for device permissions; the platform honours the flag only as boolean true.
  fetchUsersWithDevicePermissions = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<User>> =>
    this.get(`/organizations/${orgId}/users`, {
      params: { $filters: JSON.stringify({ includeDevicePermissions: true }), $limit: -1 },
      ...config,
    });

  fetchCalendarSyncStatus = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<CalendarSyncAffectedUser>> =>
    this.get(`/organizations/${orgId}/nylas/sync-status`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  getUserLogins = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<UserLogin>> => this.get(`/organizations/${orgId}/user-logins`, config);

  updateMe = (
    payload: OnlyRequiredFields<Nullable<User>, "id">,
    config?: RequestOptions
  ): Promise<User> => this.put("/users/me", payload, config);

  getOrganization = (orgId: Organization["id"], config?: RequestOptions): Promise<Organization> =>
    this.get(`/organizations/${orgId}`, config);

  getTeam = (
    orgId: Organization["id"],
    teamId: Team["id"],
    config?: RequestOptions
  ): Promise<Team> => this.get(`/organizations/${orgId}/teams/${teamId}`, config);

  updateOrganization = (
    payload: OnlyRequiredFields<Organization, "id">,
    config?: RequestOptions
  ): Promise<Organization> => this.put(`/organizations/${payload.id}`, payload, config);

  updateOrganizationSetting = (
    orgId: Organization["id"],
    payload: Setting,
    config?: RequestOptions
  ): Promise<Setting> =>
    this.put(`/organizations/${orgId}/settings/${payload.id}`, payload, config);

  updateTeam = (
    orgId: Organization["id"],
    payload: OnlyRequiredFields<Team, "id">,
    config?: RequestOptions
  ): Promise<Team> => this.put(`/organizations/${orgId}/teams/${payload.id}`, payload, config);

  updateUser = (
    orgId: Organization["id"],
    payload: OnlyRequiredFields<User, "id">,
    config?: RequestOptions
  ): Promise<User> => this.put(`/organizations/${orgId}/users/${payload.id}`, payload, config);

  fetchGeocodeLimits = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<GeocodeLimits> => this.get(`organizations/${orgId}/geocode/limits`, config);

  fetchCurrencies = (): Promise<SimpleListResponse<Currency>> => this.get("currencies?$limit=1000");

  fetchCustomFields = async (
    orgId: Organization["id"],
    entityType?: EntityTypeSupportingCustomFields,
    config: RequestOptions = { params: { $limit: 1000, $order: "displayOrder" } }
  ): Promise<SimpleListResponse<CustomField>> => {
    const response: SimpleListResponse<CustomField> = await this.get(
      entityType
        ? `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}`
        : `/organizations/${orgId}/custom-fields`,
      config
    );

    // sometimes custom fields have equal display order, here we guarantee that each field will have a unique display order

    const entityTypeToIndex: { [key in EntityTypeSupportingCustomFields]: number } = {
      [EntityType.ACTIVITY]: 1,
      [EntityType.COMPANY]: 1,
      [EntityType.DEAL]: 1,
      [EntityType.PERSON]: 1,
    };

    response.data.forEach((customField, index) => {
      customField.displayOrder = entityType ? index + 1 : entityTypeToIndex[customField.entity]++;
    });
    return response;
  };

  deleteCustomField = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    id: CustomField["id"],
    sendReport: boolean,
    config?: RequestOptions
  ): Promise<SimpleListResponse<CustomField>> =>
    this.delete(`/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}/${id}`, {
      ...config,
      ...(sendReport ? { params: { $filters: JSON.stringify({ export: true }) } } : {}),
    });

  createCustomField = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    customField: CustomFieldPayload,
    config?: RequestOptions
  ): Promise<CustomField> =>
    this.post(
      `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}`,
      customField,
      config
    );

  updateCustomField = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    customField: CustomField,
    config?: RequestOptions
  ): Promise<CustomField> =>
    this.put(
      `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}/${customField.id}`,
      customField,
      config
    );

  updateCustomFieldBulk = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    customFields: CustomField[],
    config?: RequestOptions
  ): Promise<SimpleListResponse<CustomField>> =>
    this.put(
      `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}/bulk`,
      customFields,
      config
    );

  private prepareUpsertCustomFieldsPayload(
    entityType: EntityTypeSupportingCustomFields,
    entityId: EntitySupportingCustomFields["id"] | undefined,
    customFieldsValues: CustomFieldValue[]
  ) {
    return [
      {
        customFieldData: customFieldsValues.map(({ customField, value }) => ({
          customField,
          value: value ?? [],
        })),
        [upsertCustomFieldPayloadPart[entityType]]: entityId ?? null,
      },
    ];
  }

  validateCustomFieldsValues = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    entityType: EntityTypeSupportingCustomFields,
    entityId: EntitySupportingCustomFields["id"] | undefined,
    customFieldsValues: CustomFieldValue[],
    config?: RequestOptions
  ): Promise<FieldValidationErrors[]> => {
    const url = `/organizations/${orgId}/${entityType}/custom-field-values/upsert`;
    const response: CustomFieldValuesUpsertResponse[] = await this.post(
      url,
      this.prepareUpsertCustomFieldsPayload(entityType, entityId, customFieldsValues),
      {
        ...config,
        params: {
          ...config?.params,
          $limit: 1000,
          $order: "displayOrder",
          layoutId,
          validateOnly: true,
        },
      }
    );

    return response[0].errorCF.filter(({ error }) => error.length);
  };

  upsertCustomFieldsValues = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    entityType: EntityTypeSupportingCustomFields,
    entityId: EntitySupportingCustomFields["id"] | undefined,
    customFieldsValues: CustomFieldValue[],
    config?: RequestOptions
  ): Promise<ListResponse<CustomField>> => {
    const url = `/organizations/${orgId}/${entityType}/custom-field-values/upsert`;
    return this.put(
      url,
      this.prepareUpsertCustomFieldsPayload(entityType, entityId, customFieldsValues),
      {
        ...config,
        params: {
          ...config?.params,
          $limit: 1000,
          $order: "displayOrder",
          layoutId,
        },
      }
    );
  };

  // POST marks this as a "create" save so the backend skips edit-only field-visibility
  // enforcement — view-only custom fields are fillable when a record is first created.
  createCustomFieldsValues = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    entityType: EntityTypeSupportingCustomFields,
    entityId: EntitySupportingCustomFields["id"] | undefined,
    customFieldsValues: CustomFieldValue[],
    config?: RequestOptions
  ): Promise<ListResponse<CustomField>> => {
    const url = `/organizations/${orgId}/${entityType}/custom-field-values/upsert`;
    return this.post(
      url,
      this.prepareUpsertCustomFieldsPayload(entityType, entityId, customFieldsValues),
      {
        ...config,
        params: {
          ...config?.params,
          $limit: 1000,
          $order: "displayOrder",
          layoutId,
        },
      }
    );
  };

  bulkValidateCustomFieldsValues = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    payload: CustomFieldValuesUpsertPayload[],
    config?: RequestOptions
  ): Promise<ListResponse<CustomField>> => {
    return this.post(`/organizations/${orgId}/${entityType}/custom-field-values/upsert`, payload, {
      ...config,
      params: {
        ...config?.params,
        validateOnly: true,
      },
    });
  };

  bulkUpsertCustomFieldsValues = (
    update: boolean,
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    entitiesInfo: EntityIdWithLayoutId<EntitySupportingCustomFields>[],
    customFieldValues: CustomFieldValue[],
    config?: RequestOptions
  ): Promise<CustomFieldValuesUpsertResponse[]> => {
    const customFieldData = customFieldValues.map(({ customField, value }) => ({
      customField,
      value: value ?? [],
    }));

    const payload = entitiesInfo.map(({ id, layoutId }) => ({
      customFieldData,
      layoutId,
      [upsertCustomFieldPayloadPart[entityType]]: id,
    }));

    return (update ? this.put.bind(this) : this.post.bind(this))(
      `/organizations/${orgId}/${entityType}/custom-field-values/upsert`,
      payload,
      config
    );
  };

  createActivityType = async (
    orgId: Organization["id"],
    payload: ActivityTypePayload,
    config?: RequestOptions
  ): Promise<ActivityType> =>
    this.post(`/organizations/${orgId}/crm-activity-types`, payload, config);

  updateActivityType = async (
    orgId: Organization["id"],
    payload: OnlyRequiredFields<ActivityType, "id">,
    config?: RequestOptions
  ): Promise<ActivityType> =>
    this.put(`/organizations/${orgId}/crm-activity-types/${payload.id}`, payload, config);

  deleteActivityType = async (
    orgId: Organization["id"],
    activityTypeId: ActivityType["id"]
  ): Promise<ActivityType> =>
    this.delete(`/organizations/${orgId}/crm-activity-types/${activityTypeId}`);

  fetchGroups = (
    orgId: Organization["id"],
    entityType?: EntityTypeSupportingGroups,
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">> = {
      $order: "name",
    },
    config?: RequestOptions
  ): Promise<ListResponse<Group>> =>
    this.get(
      entityType
        ? `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}`
        : `/organizations/${orgId}/groups`,
      {
        params: {
          $filters: $filters ? JSON.stringify($filters) : undefined,
          $limit,
          $offset,
          $order,
        },
        ...config,
      }
    );

  fetchGroup = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    groupId: Group["id"],
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Group> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.get(
      `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}/${groupId}`,
      {
        params: {
          $filters: JSON.stringify($filters),
        },
        ...config,
      }
    );
  };

  fetchActivityTypes = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<ActivityType>> =>
    this.get(`/organizations/${orgId}/crm-activity-types`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchActivities = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Activity>> =>
    this.get(`/organizations/${orgId}/crm-activities`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchDistance = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<DistanceRecord>> =>
    this.get(`/organizations/${orgId}/distance`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  listCoachingNotes = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<CoachingNote>> =>
    this.get(`/organizations/${orgId}/coaching-notes`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchFaceTime = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<FaceTime>> =>
    this.get(`/organizations/${orgId}/face-time`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  createCoachingNote = (
    orgId: Organization["id"],
    payload: {
      answers?: Array<Pick<CoachingNoteAnswer, "promptId" | "value">>;
      categoryId?: null | number;
      coachingFileIds?: Array<CoachingFile["id"]>;
      note: string;
      parentNoteId?: null | number;
      subjectUserId: User["id"];
      visibility?: CoachingNoteVisibility;
    },
    config?: RequestOptions
  ): Promise<CoachingNote> => this.post(`/organizations/${orgId}/coaching-notes`, payload, config);

  updateCoachingNote = (
    orgId: Organization["id"],
    noteId: CoachingNote["id"],
    payload: {
      answers?: Array<Pick<CoachingNoteAnswer, "promptId" | "value">>;
      categoryId?: null | number;
      coachingFileIds?: Array<CoachingFile["id"]>;
      note?: string;
      visibility?: CoachingNoteVisibility;
    },
    config?: RequestOptions
  ): Promise<CoachingNote> =>
    this.put(
      `/organizations/${orgId}/coaching-notes/${noteId}`,
      { id: noteId, ...payload },
      config
    );

  deleteCoachingNote = (
    orgId: Organization["id"],
    noteId: CoachingNote["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/coaching-notes/${noteId}`, config);

  listCoachingNoteCategories = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<CoachingNoteCategory>> =>
    this.get(`/organizations/${orgId}/coaching-note-categories`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  createCoachingNoteCategory = (
    orgId: Organization["id"],
    payload: { name: string },
    config?: RequestOptions
  ): Promise<CoachingNoteCategory> =>
    this.post(`/organizations/${orgId}/coaching-note-categories`, payload, config);

  updateCoachingNoteCategory = (
    orgId: Organization["id"],
    categoryId: CoachingNoteCategory["id"],
    payload: Partial<Pick<CoachingNoteCategory, "displayOrder" | "name" | "prompts">>,
    config?: RequestOptions
  ): Promise<CoachingNoteCategory> =>
    this.put(
      `/organizations/${orgId}/coaching-note-categories/${categoryId}`,
      { id: categoryId, ...payload },
      config
    );

  deleteCoachingNoteCategory = (
    orgId: Organization["id"],
    categoryId: CoachingNoteCategory["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/coaching-note-categories/${categoryId}`, config);

  listCoachingFiles = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<CoachingFile>> =>
    this.get(`/organizations/${orgId}/coaching-files`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  // Single-record GET returns the row with a presigned `publicURI` / `streamUrl`
  fetchCoachingFile = (
    orgId: Organization["id"],
    coachingFileId: CoachingFile["id"],
    config?: RequestOptions
  ): Promise<CoachingFile> =>
    this.get(`/organizations/${orgId}/coaching-files/${coachingFileId}`, config);

  // BE registers POST at the nested path `/users/:subjectUserId/coaching-files`
  createCoachingFile = (
    orgId: Organization["id"],
    file: Blob | File,
    subjectUserId: User["id"],
    config?: RequestOptions
  ): Promise<CoachingFile> =>
    this.uploadFileAsFormData(
      `/organizations/${orgId}/users/${subjectUserId}/coaching-files`,
      file,
      new FormData(),
      config
    );

  updateCoachingFile = (
    orgId: Organization["id"],
    coachingFileId: CoachingFile["id"],
    payload: CoachingFileUpdatePayload,
    config?: RequestOptions
  ): Promise<CoachingFile> =>
    this.put(
      `/organizations/${orgId}/coaching-files/${coachingFileId}`,
      { id: coachingFileId, ...payload },
      config
    );

  deleteCoachingFile = (
    orgId: Organization["id"],
    coachingFileId: CoachingFile["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/coaching-files/${coachingFileId}`, config);

  fetchMappedActivities = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<MapEntry>> =>
    this.get(`/organizations/${orgId}/crm-activities`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchLeaderboardData = (
    orgId: Organization["id"],
    filters: PlatformFilterModel &
      Partial<{
        date: PlatformSimpleCondition;
        includePerformanceMetric: boolean;
        property: { $in: (CustomMetric["name"] | LeaderboardMetricFieldName)[] };
        summary: "all" | "team" | "user";
      }>,
    order: string | undefined,
    config?: RequestOptions
  ): Promise<ListResponse<LeaderboardItem>> =>
    this.get(`/organizations/${orgId}/stats?inAppLeaderboard=true`, {
      params: {
        $filters: filters ? JSON.stringify(filters) : undefined,
        $limit: 1000,
        $order: order,
      },
      ...config,
    });

  fetchActivitiesAndSuggestions = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<ActivityOrSuggestion>> =>
    this.get(`/organizations/${orgId}/crm-activities`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: JSON.stringify({ ...($filters ?? {}), includeSuggestions: true }),
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchCompanies = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Company>> =>
    this.get(`/organizations/${orgId}/accounts`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchEntityDuplicates = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingDataMerging,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<DuplicateEntity>> =>
    this.get(`/organizations/${orgId}/${entityTypeToDuplicateSearch[entityType]}`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  updateCompany = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"] | undefined,
    payload: OnlyRequiredFields<UpdateCompanyPayload, "id">,
    options?: {
      cadence?: boolean;
      includeAccessStatus?: boolean;
      includeCustomFields?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeRoutes?: boolean;
      includeTerritories?: boolean;
      includeUsersWithAccess?: boolean;
    },
    // set to false in cases when we ignore the result anyway, or when we
    // expect fetch to fail, e.g. due to changed owner
    fetchCompany: boolean = true,
    config?: RequestOptions
  ): Promise<Company> => {
    if (!layoutId) {
      loggingService.debug(`Layout ID should be provided in updateCompany request`, { payload });
    }
    const updatedCompany: Company = await this.put(
      `/organizations/${orgId}/accounts/${payload.id}`,
      payload,
      {
        ...config,
        params: { ...config?.params, ignoreLayout: layoutId ? undefined : true, layoutId },
      }
    );
    if (!fetchCompany) {
      return updatedCompany;
    }
    const getOptions = { includeAccessStatus: true, ...(options ?? {}) };
    return this.fetchCompany(orgId, payload.id, getOptions);
  };

  cumulDemoRequest = async (
    token: string,
    message: string,
    config?: RequestOptions
  ): Promise<void> =>
    this.post(
      `/email/mmc`,
      {
        message,
        subject: "A customer is interested in Advanced Reporting [Internal Alert]",
        token,
      },
      config
    );

  fetchFunnels = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Funnel>> =>
    this.get(`/organizations/${orgId}/funnels`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchFunnelSummary = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<StageSummary>> =>
    this.get(`organizations/${orgId}/reports/funnels/${funnelId}/summary`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  fetchLocationSummary = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<Location>> =>
    this.get(`/organizations/${orgId}/reports/locations/summary`, config);

  fetchStages = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Stage>> =>
    this.get(`/organizations/${orgId}/funnels/${funnelId}/stages`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchDeals = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Deal>> =>
    this.get(`/organizations/${orgId}/deals`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchPeople = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Person>> =>
    this.get(`/organizations/${orgId}/contacts`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  createPerson = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: CreatePersonPayload,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Company> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.post(`/organizations/${orgId}/contacts`, payload, {
      params: {
        $filters: JSON.stringify($filters),
        layoutId,
      },
      ...config,
    });
  };

  updatePerson = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"] | undefined,
    payload: OnlyRequiredFields<UpdatePersonPayload, "id">,
    options?: {
      cadence?: boolean;
      includeAccessStatus?: boolean;
      includeCustomFields?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeRoutes?: boolean;
      includeUsersWithAccess?: boolean;
    },
    // set to false in cases when we ignore the result anyway, or when we
    // expect fetch to fail, e.g. due to changed owner
    fetchPerson: boolean = true,
    config?: RequestOptions
  ): Promise<Person> => {
    if (!layoutId) {
      loggingService.debug(`Layout ID should be provided in updatePerson request`, { payload });
    }
    const updatedPerson: Person = await this.put(
      `/organizations/${orgId}/contacts/${payload.id}`,
      payload,
      {
        ...config,
        params: { ...config?.params, ignoreLayout: layoutId ? undefined : true, layoutId },
      }
    );
    if (!fetchPerson) {
      return updatedPerson;
    }
    const getOptions = { includeAccessStatus: true, ...(options ?? {}) };
    return this.fetchPerson(orgId, payload.id, getOptions);
  };

  fetchUnifiedRoutes = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Route>> =>
    this.get(`/organizations/${orgId}/smart-routes`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchUnifiedRoutesAggregated = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<AggregatedListResponse<Route>> =>
    this.get(`/organizations/${orgId}/smart-routes`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchUnifiedRoute = (
    orgId: Organization["id"],
    routeId: Route["id"],
    options: { includeAccessStatus?: boolean; includePolyline?: boolean } = {
      includeAccessStatus: true,
    },
    config?: RequestOptions
  ): Promise<Route> => {
    // includePolyline is opt-in on the BE (large field, _source-excluded by default). Route
    // detail is the one read that draws the line, and it is what lets the scene reuse the
    // stored solve instead of buying a fresh preview on every open.
    const $filters = { includeAccessStatus: true, includePolyline: true, ...options };
    return this.get(`/organizations/${orgId}/smart-routes/${routeId}`, {
      params: { $filters: JSON.stringify($filters) },
      ...config,
    });
  };

  createUnifiedRoute = (
    orgId: Organization["id"],
    payload: CreateRoutePayload,
    options: { includeAccessStatus?: boolean } = {},
    config?: RequestOptions
  ): Promise<Route> => {
    const $filters = { includeAccessStatus: true, ...options };
    return this.post(`/organizations/${orgId}/smart-routes`, payload, {
      params: { $filters: JSON.stringify($filters) },
      ...config,
    });
  };

  updateUnifiedRoute = (
    orgId: Organization["id"],
    routeId: Route["id"],
    payload: UpdateRoutePayload,
    config?: RequestOptions
  ): Promise<Route> =>
    this.put(`/organizations/${orgId}/smart-routes/${routeId}`, payload, {
      ...config,
      params: { ...config?.params, $filters: JSON.stringify({ includeAccessStatus: true }) },
    });

  deleteUnifiedRoute = (
    orgId: Organization["id"],
    routeId: Route["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/smart-routes/${routeId}`, config);

  cloneUnifiedRoute = (
    orgId: Organization["id"],
    routeId: Route["id"],
    config?: RequestOptions
  ): Promise<Route> =>
    this.post(
      `/organizations/${orgId}/smart-routes/clone`,
      { id: routeId },
      {
        ...config,
        params: { ...config?.params, $filters: JSON.stringify({ includeAccessStatus: true }) },
      }
    );

  previewUnifiedRoute = (
    orgId: Organization["id"],
    payload: PreviewRoutePayload,
    config?: RequestOptions
  ): Promise<RoutePreview> =>
    this.post(`/organizations/${orgId}/smart-routes/preview`, payload, config);

  /**
   * Bulk create/update/delete route stops.
   *
   * ⚠️ Omitting `metrics` makes the BE **clear** the persisted preview columns
   * (`polyline` / `totalDistance` / `totalDuration` / `legMetrics`) — a stop change
   * invalidates them. Pass fresh metrics (from the route's current preview) to preserve them.
   *
   * This is the ONLY endpoint that accepts `legMetrics`: the route-level PUT column whitelist
   * drops it. Build the envelope with `buildRoutePreviewMetrics` so the leg array is ordered to
   * match the stop list the write leaves behind — the BE drops an array whose length cannot
   * describe that list.
   */
  bulkUpdateUnifiedRouteStops = (
    orgId: Organization["id"],
    routeId: Route["id"],
    stopEdits: RouteStopBulkItem[],
    metrics?: RoutePreviewMetrics,
    config?: RequestOptions
  ): Promise<RouteStop[]> =>
    this.post(
      `/organizations/${orgId}/smart-routes/${routeId}/stops/bulk`,
      { routeGroups: stopEdits, ...(metrics ? { routeDetail: metrics } : {}) },
      config
    );

  transferRouteStops = (
    orgId: Organization["id"],
    payload: TransferStopsPayload,
    config?: RequestOptions
  ): Promise<TransferStopsResponse> =>
    this.post(`/organizations/${orgId}/smart-routes/transfer-stops`, payload, config);

  fetchLocations = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Location>> =>
    this.get(`/organizations/${orgId}/locations`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchAllFunnelSummary = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest>,
    config?: RequestOptions
  ): Promise<ListResponse<StageSummary>> =>
    this.get(`/organizations/${orgId}/reports/funnels/${funnelId}/summary`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchReports = async (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Report>> => {
    const response: ListResponse<Report> = await this.get(`/organizations/${orgId}/reports`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

    // this hack is needed because reports endpoint returns "activities" instead of "crm-activities"
    response.data.forEach((report) => {
      if ((report.tableName as string) === "activities") {
        report.tableName = EntityType.ACTIVITY;
      }
    });

    return response;
  };

  createIntegration = (
    orgId: Organization["id"],
    payload: CreateIntegrationPayload,
    config?: RequestOptions
  ): Promise<Integration> => this.post(`/organizations/${orgId}/integrations`, payload, config);

  fetchSalesforceAuthUrl = (
    orgId: Organization["id"],
    envType: NonNullable<CreateIntegrationPayload["envType"]>,
    config?: RequestOptions
  ): Promise<SalesforceAuthUrl> =>
    this.get(`/organizations/${orgId}/integrations/salesforce/auth-url`, {
      ...config,
      params: { ...config?.params, envType },
    });

  fetchIntegrations = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<IntegrationRaw>> =>
    this.get(`/organizations/${orgId}/integrations`, config);

  fetchIntegration = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    config?: RequestOptions
  ): Promise<IntegrationRaw> =>
    this.get(`/organizations/${orgId}/integrations/${integrationId}`, config);

  deleteIntegration = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    config?: RequestOptions
  ): Promise<Integration> =>
    this.delete(`/organizations/${orgId}/integrations/${integrationId}`, config);

  updateIntegration = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    payload: Pick<Integration, "id" | "isLocked" | "syncOptions">,
    config?: RequestOptions
  ): Promise<Integration> =>
    this.put(`/organizations/${orgId}/integrations/${integrationId}`, payload, config);

  reauthenticateIntegration = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    payload: ReauthenticateIntegrationPayload,
    config?: RequestOptions
  ): Promise<IntegrationRaw> =>
    this.post(
      `/organizations/${orgId}/integrations/${integrationId}/reauthenticate`,
      payload,
      config
    );

  fetchIntegrationUsers = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest>,
    config?: RequestOptions
  ): Promise<ListResponse<IntegrationUser>> =>
    this.get(`/organizations/${orgId}/integrations/${integrationId}/users`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order: Array.isArray($order) ? $order.join(",") : $order ? $order : undefined,
      },
      ...config,
    });

  fetchIntegrationSyncs = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest>,
    config?: RequestOptions
  ): Promise<ListResponse<IntegrationSync>> =>
    this.get(`/organizations/${orgId}/integrations/${integrationId}/integration-syncs`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  updateIntegrationUsers = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    payload: Pick<IntegrationUser, "id" | "syncing" | "userId">[],
    config?: RequestOptions
  ): Promise<ListResponse<IntegrationUser>> =>
    this.put(
      `/organizations/${orgId}/integrations/${integrationId}/users/bulk-update`,
      payload,
      config
    );

  fetchIntegrationFields = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<IntegrationField>> =>
    this.get(`/organizations/${orgId}/integrations/${integrationId}/fields`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  updateIntegrationFields = (
    orgId: Organization["id"],
    integrationId: Integration["id"],
    payload: Partial<Record<EntityTypesSupportedByIntegrations, PlatformIntegrationField[]>>,
    config?: RequestOptions
  ): Promise<Partial<Record<EntityTypesSupportedByIntegrations, IntegrationFieldResponse[]>>> =>
    this.put(
      `/organizations/${orgId}/integrations/${integrationId}/fields/mapping`,
      payload,
      config
    );

  generateIntegrationPemFile = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<string> =>
    this.post(`/organizations/${orgId}/integrations/generate-pem-files`, config);

  downloadIntegrationPemFile = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<string> =>
    this.get(`/organizations/${orgId}/integrations/download-public-pem-file`, config);

  fetchReport = async (
    orgId: Organization["id"],
    reportId: Report["id"],
    config?: RequestOptions
  ): Promise<Report> => this.get(`/organizations/${orgId}/reports/${reportId}`, config);

  updateReport = async (
    orgId: Organization["id"],
    report: Partial<Report>,
    config?: RequestOptions
  ): Promise<Report> => this.put(`/organizations/${orgId}/reports/${report.id}`, report, config);

  createReport = async (
    orgId: Organization["id"],
    report: Omit<Partial<Report>, "id">,
    config?: RequestOptions
  ): Promise<Report> => this.post(`/organizations/${orgId}/reports`, report, config);

  generateReport = async (
    orgId: Organization["id"],
    reportId: Report["id"],
    email: string,
    config?: RequestOptions
  ): Promise<void> => {
    await this.post(
      `/organizations/${orgId}/reports/${reportId}/generate`,
      { username: email },
      config
    );
  };

  cloneReport = async (
    orgId: Organization["id"],
    reportId: Report["id"],
    config?: RequestOptions
  ): Promise<Report> => {
    return this.post(`/organizations/${orgId}/reports/clone`, { id: reportId }, config);
  };

  deleteReport = async (
    orgId: Organization["id"],
    reportId: Report["id"],
    config?: RequestOptions
  ): Promise<void> => {
    await this.delete(`/organizations/${orgId}/reports/${reportId}`, config);
  };

  fetchCompany = (
    orgId: Organization["id"],
    id: Company["id"],
    options?: {
      cadence?: boolean;
      includeAccessStatus?: boolean;
      includeAssociations?: boolean;
      includeCustomFields?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeRoutes?: boolean;
      includeTerritories?: boolean;
      includeUsersWithAccess?: boolean;
      pinLegends?: PinLegendsFilter;
    },
    config?: RequestOptions
  ): Promise<Company> =>
    this.get(`/organizations/${orgId}/accounts/${id}`, {
      params: {
        $filters: options ? JSON.stringify(options) : undefined,
      },
      ...config,
    });

  deleteCompany = (
    orgId: Organization["id"],
    id: Company["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/accounts/${id}`, config);

  fetchPerson = (
    orgId: Organization["id"],
    id: Person["id"],
    options?: {
      cadence?: boolean;
      includeAccessStatus?: boolean;
      includeCustomFields?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeRoutes?: boolean;
      includeTerritories?: boolean;
      includeUsersWithAccess?: boolean;
      pinLegends?: PinLegendsFilter;
    },
    config?: RequestOptions
  ): Promise<Person> =>
    this.get(`/organizations/${orgId}/contacts/${id}`, {
      params: {
        $filters: options ? JSON.stringify(options) : undefined,
      },
      ...config,
    });

  deletePerson = (
    orgId: Organization["id"],
    id: Person["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/contacts/${id}`, config);

  fetchDeal = (
    orgId: Organization["id"],
    id: Deal["id"],
    options?: {
      includeAccessStatus?: boolean;
      includeCustomFields?: boolean;
      includeFiles?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeTerritories?: boolean;
      includeUsersWithAccess?: boolean;
      pinLegends?: PinLegendsFilter;
    },
    config?: RequestOptions
  ): Promise<Deal> =>
    this.get(`/organizations/${orgId}/deals/${id}`, {
      params: {
        $filters: options ? JSON.stringify(options) : undefined,
      },
      ...config,
    });

  deleteDeal = (
    orgId: Organization["id"],
    id: Deal["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/deals/${id}`, config);

  changePassword = (
    password: string,
    currentPassword: string,
    config?: RequestOptions
  ): Promise<void> => this.put("/users/me/password", { currentPassword, password }, config);

  createFile = (
    orgId: Organization["id"],
    file: Blob | File,
    isPublic = false,
    entityType?: EntityType | undefined,
    entityId?: AnyEntityId | undefined,
    config?: RequestOptions
  ): Promise<RawFile> => {
    const endpoint = `/organizations/${orgId}${
      entityType && entityId ? `/${entityType}/${entityId}` : ""
    }/files`;
    const formData = new FormData();
    if (isPublic) {
      formData.append("isPublic", true.toString());
    }
    return this.uploadFileAsFormData(endpoint, file, formData, config);
  };

  fetchFilePreview = (
    orgId: Organization["id"],
    file: File,
    config?: RequestOptions
  ): Promise<FilePreview> => {
    const formData = new FormData();
    formData.append("file", file);
    return this.uploadFileAsFormData(
      `/organizations/${orgId}/imports/preview`,
      file,
      formData,
      config
    );
  };

  fetchFile = (
    orgId: Organization["id"],
    fileId: RawFile["id"],
    download: boolean,
    preview: boolean,
    entityType?: EntityType,
    entityId?: AnyEntityId,
    config?: RequestOptions
  ): Promise<Blob | RawFile> => {
    const fileUrl = getFilesEndpoint(orgId, fileId, entityType, entityId, download, preview);
    return this.get(fileUrl, config);
  };

  fetchFiles = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<SimpleListResponse<RawFile>> => {
    const options = {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    };
    return this.get(getFilesEndpoint(orgId), options);
  };

  fetchEntityFiles = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingFiles,
    entityId: EntitiesSupportingFiles["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<SimpleListResponse<RawFile>> => {
    const options = {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    };
    if (entityType === EntityType.COMPANY) {
      return this.get(`/organizations/${orgId}/accounts/${entityId}/files`, options);
    } else if (entityType === EntityType.PERSON) {
      return this.get(`/organizations/${orgId}/contacts/${entityId}/files`, options);
    } else if (entityType === EntityType.DEAL) {
      return this.get(`/organizations/${orgId}/deals/${entityId}/files`, options);
    } else if (entityType === EntityType.ACTIVITY) {
      return this.get(`/organizations/${orgId}/crm-activities/${entityId}/files`, options);
    } else if (entityType === EntityType.TRIP) {
      return this.get(`/organizations/${orgId}/trips/${entityId}/files`, options);
    } else {
      invariant(false, `Unsupported entity type in fetchEntityFiles: ${entityType}`);
    }
  };

  deleteEntityFile = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingFiles,
    entityId: EntitiesSupportingFiles["id"],
    fileId: RawFile["id"],
    config?: RequestOptions
  ): Promise<void> => {
    if (entityType === EntityType.COMPANY) {
      return this.delete(`/organizations/${orgId}/accounts/${entityId}/files/${fileId}`, config);
    } else if (entityType === EntityType.PERSON) {
      return this.delete(`/organizations/${orgId}/contacts/${entityId}/files/${fileId}`, config);
    } else if (entityType === EntityType.DEAL) {
      return this.delete(`/organizations/${orgId}/deals/${entityId}/files/${fileId}`, config);
    } else if (entityType === EntityType.ACTIVITY) {
      return this.delete(
        `/organizations/${orgId}/crm-activities/${entityId}/files/${fileId}`,
        config
      );
    } else if (entityType === EntityType.TRIP) {
      return this.delete(`/organizations/${orgId}/trips/${entityId}/files/${fileId}`, config);
    } else {
      invariant(false, `Unsupported entity type in deleteEntityFiles: ${entityType}`);
    }
  };

  requestPasswordReset = (username: string, config?: RequestOptions): Promise<void> =>
    this.post("/request-password-reset", { username }, config);

  resetPassword = (
    code: string,
    password: string,
    username: string,
    config?: RequestOptions
  ): Promise<void> => this.post("/reset-password", { code, password, username }, config);

  // returns error code 200 if valid or 400 if not
  checkResetPasswordCodeStatus = (
    code: string,
    username: string,
    config?: RequestOptions
  ): Promise<void> => this.get("/reset-password/status", { params: { code, username }, ...config });

  fetchSsoLoginRedirectUri = (username: string, config?: RequestOptions): Promise<string> =>
    this.post("/sso/login", { username }, config);

  search = (
    orgId: Organization["id"],
    searchText: string,
    entityTypes?: string[]
  ): Promise<SimpleListResponse<TextSearchResult>> => {
    if (!searchText.trim().length) {
      return Promise.resolve({ count: 0, data: [], limit: 0, offset: 0, total: 0 });
    }

    const filters = { $q: searchText, entityType: entityTypes };
    return this.get(`/organizations/${orgId}/full-search`, {
      params: { $filters: JSON.stringify(filters) },
    });
  };

  userExists = async (username: string, config?: RequestOptions): Promise<boolean> => {
    const response = await this.post("/users/exists", { username }, config);
    return response.userExists;
  };

  createNote = async (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingNotes,
    entityId: AnyEntityId,
    note: string,
    config?: RequestOptions
  ): Promise<Note> => {
    if (entityType === EntityType.COMPANY) {
      return this.post(`/organizations/${orgId}/accounts/${entityId}/notes`, { note }, config);
    } else if (entityType === EntityType.PERSON) {
      return this.post(`/organizations/${orgId}/contacts/${entityId}/notes`, { note }, config);
    } else if (entityType === EntityType.DEAL) {
      return this.post(`/organizations/${orgId}/deals/${entityId}/notes`, { note }, config);
    } else {
      invariant(false, `Unsupported entity type in createNote: ${entityType}`);
    }
  };

  updateNote = async (
    entityType: EntityTypeSupportingNotes,
    entityId: AnyEntityId,
    note: Note,
    config?: RequestOptions
  ): Promise<Note> => {
    if (entityType === EntityType.COMPANY) {
      return this.put(`/accounts/${entityId}/notes/${note.id}`, note, config);
    } else if (entityType === EntityType.PERSON) {
      return this.put(`/contacts/${entityId}/notes/${note.id}`, note, config);
    } else if (entityType === EntityType.DEAL) {
      return this.put(`/deals/${entityId}/notes/${note.id}`, note, config);
    } else {
      invariant(false, `Unsupported entity type in updateNote: ${entityType}`);
    }
  };

  deleteNote = async (
    entityType: EntityTypeSupportingNotes,
    entityId: AnyEntityId,
    note: Note,
    config?: RequestOptions
  ): Promise<void> => {
    if (entityType === EntityType.COMPANY) {
      return this.delete(`/accounts/${entityId}/notes/${note.id}`, config);
    } else if (entityType === EntityType.PERSON) {
      return this.delete(`/contacts/${entityId}/notes/${note.id}`, config);
    } else if (entityType === EntityType.DEAL) {
      return this.delete(`/deals/${entityId}/notes/${note.id}`, config);
    } else {
      invariant(false, `Unsupported entity type in deleteNote: ${entityType}`);
    }
  };

  fetchActivity = async (
    orgId: Organization["id"],
    activityId: Activity["id"],
    options?: {
      includeAccessStatus?: boolean;
      includeAccountSharedActivities?: boolean;
      includeContactSharedActivities?: boolean;
      includeCustomFields?: boolean;
      includeDealSharedActivities?: boolean;
      includeEmails?: boolean;
      includeEmailStats?: boolean;
      includeFiles?: boolean;
      includeUsersWithAccess?: boolean;
    },
    config?: RequestOptions
  ): Promise<Activity> =>
    this.get(`/organizations/${orgId}/crm-activities/${activityId}`, {
      params: {
        $filters: options ? JSON.stringify(options) : undefined,
      },
      ...config,
    });

  createActivity = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: ActivityPayload,
    options?: {
      ignoreLayout?: boolean;
      includeAccessStatus?: boolean;
      includeFiles?: boolean;
    },
    config?: RequestOptions
  ): Promise<Activity> => {
    const { ignoreLayout, ...filterOptions } = options ?? {};
    const $filters = { includeAccessStatus: true, ...filterOptions };
    return this.post(`/organizations/${orgId}/crm-activities`, payload, {
      params: {
        $filters: JSON.stringify($filters),
        ignoreLayout: ignoreLayout || undefined,
        layoutId,
      },
      ...config,
    });
  };

  updateActivity = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"] | undefined,
    payload: Partial<ActivityPayload> & Identified,
    options?: {
      ignoreLayout?: boolean;
      includeAccessStatus?: boolean;
      includeAccountSharedActivities?: boolean;
      includeContactSharedActivities?: boolean;
      includeCustomFields?: boolean;
      includeDealSharedActivities?: boolean;
      includeFiles?: boolean;
      updateRecurring?: RecurringActionType;
    },
    config?: RequestOptions
  ): Promise<Activity> => {
    if (!layoutId) {
      loggingService.debug(`Layout ID should be provided in updateActivity request`, { payload });
    }
    const { ignoreLayout, ...filterOptions } = options ?? {};
    const $filters = { includeAccessStatus: true, ...filterOptions };
    await this.put(`/organizations/${orgId}/crm-activities/${payload.id}`, payload, {
      ...config,
      params: {
        ...config?.params,
        $filters: JSON.stringify($filters),
        ignoreLayout: ignoreLayout || (layoutId ? undefined : true),
        layoutId,
      },
    });
    return this.fetchActivity(orgId, payload.id, $filters);
  };

  deleteActivity = async (
    orgId: Organization["id"],
    activityId: Activity["id"],
    deleteRecurring?: RecurringActionType,
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/crm-activities/${activityId}`, {
      params: {
        $filters: deleteRecurring && JSON.stringify({ deleteRecurring }),
      },
      ...config,
    });

  createDeal = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: CreateDealPayload,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Report> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.post(`/organizations/${orgId}/deals`, payload, {
      params: {
        $filters: JSON.stringify($filters),
        layoutId,
      },
      ...config,
    });
  };

  updateDeal = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"] | undefined,
    payload: OnlyRequiredFields<UpdateDealPayload, "id">,
    options?: {
      includeAccessStatus?: boolean;
      includeCustomFields?: boolean;
      includeGroups?: boolean;
      includeNotes?: boolean;
      includeUsersWithAccess?: boolean;
    },
    // set to false in cases when we ignore the result anyway, or when we
    // expect fetch to fail, e.g. due to changed owner
    fetchDeal: boolean = true,
    config?: RequestOptions
  ): Promise<Deal> => {
    if (!layoutId) {
      loggingService.debug(`Layout ID should be provided in updateDeal request`, { payload });
    }
    const updatedDeal: Deal = await this.put(
      `/organizations/${orgId}/deals/${payload.id}`,
      payload,
      {
        ...config,
        params: { ...config?.params, ignoreLayout: layoutId ? undefined : true, layoutId },
      }
    );
    if (!fetchDeal) {
      return updatedDeal;
    }
    const getOptions = { includeAccessStatus: true, ...(options ?? {}) };
    return this.fetchDeal(orgId, payload.id, getOptions);
  };

  fetchDealLossReasons = async (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<DealLossReason>> =>
    this.get(`/organizations/${orgId}/deal-loss-reasons`, config);

  createDealLossReason = async (
    orgId: Organization["id"],
    { displayOrder, funnelId, name }: CreateDealLossReasonPayload,
    config?: RequestOptions
  ): Promise<DealLossReason> =>
    this.post(
      `/organizations/${orgId}/deal-loss-reasons`,
      { displayOrder, funnelId, name },
      config
    );

  deleteDealLossReason = async (
    orgId: Organization["id"],
    dealLossReasonId: DealLossReason["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/deal-loss-reasons/${dealLossReasonId}`, config);

  updateDealLossReason = async (
    orgId: Organization["id"],
    payload: DealLossReason,
    config?: RequestOptions
  ): Promise<DealLossReason> =>
    this.put(`/organizations/${orgId}/deal-loss-reasons/${payload.id}`, payload, config);

  addToGroup = async (
    orgId: Organization["id"],
    groupId: Group["id"],
    entityType: EntityType,
    entityIds: AnyEntityId[],
    config?: RequestOptions
  ): Promise<void> => {
    if (entityType === EntityType.COMPANY) {
      await this.post(
        `/organizations/${orgId}/account-groups/${groupId}/accounts/bulk-create`,
        {
          accountGroups: entityIds.map((accountId) => ({ accountId })),
        },
        config
      );
    } else if (entityType === EntityType.PERSON) {
      await this.post(
        `/organizations/${orgId}/contact-groups/${groupId}/contacts/bulk-create`,
        {
          contactGroups: entityIds.map((contactId) => ({ contactId })),
        },
        config
      );
    } else if (entityType === EntityType.DEAL) {
      await this.post(
        `/organizations/${orgId}/deal-groups/${groupId}/deals/bulk-create`,
        {
          dealGroups: entityIds.map((dealId) => ({ dealId })),
        },
        config
      );
    } else {
      invariant(false, `Unsupported entity type in addToGroup: ${entityType}`);
    }
  };

  deleteFromGroup = async (
    orgId: Organization["id"],
    groupId: Group["id"],
    entityType: EntityTypeSupportingGroups,
    entityIds: AnyEntityId[],
    config?: RequestOptions
  ): Promise<void> => {
    if (entityType === EntityType.COMPANY) {
      await this.delete(`/organizations/${orgId}/account-groups/${groupId}/accounts/bulk-delete`, {
        data: {
          accountGroups: entityIds.map((accountId) => ({ accountId })),
        },
        ...config,
      });
    } else if (entityType === EntityType.PERSON) {
      await this.delete(`/organizations/${orgId}/contact-groups/${groupId}/contacts/bulk-delete`, {
        data: {
          contactGroups: entityIds.map((contactId) => ({ contactId })),
        },
        ...config,
      });
    } else if (entityType === EntityType.DEAL) {
      await this.delete(`/organizations/${orgId}/deal-groups/${groupId}/deals/bulk-delete`, {
        data: {
          dealGroups: entityIds.map((dealId) => ({ dealId })),
        },
        ...config,
      });
    } else {
      invariant(false, `Unsupported entity type in deleteFromGroup: ${entityType}`);
    }
  };

  updateEntities = async (
    orgId: Organization["id"],
    entityType: EntityType,
    entities: Partial<AnyEntity>[],
    config?: RequestOptions
  ): Promise<BulkUpdateResponse> => {
    if (entityType === EntityType.COMPANY) {
      return this.put(
        `/organizations/${orgId}/accounts/bulk-update`,
        { accountIds: entities },
        config
      );
    } else if (entityType === EntityType.PERSON) {
      return this.put(
        `/organizations/${orgId}/contacts/bulk-update`,
        { contactIds: entities },
        config
      );
    } else if (entityType === EntityType.DEAL) {
      return this.put(`/organizations/${orgId}/deals/bulk-update`, { dealIds: entities }, config);
    } else if (entityType === EntityType.TRIP) {
      return this.put(`/organizations/${orgId}/trips/bulk`, entities, config);
    } else if (entityType === EntityType.ACTIVITY) {
      return this.put(
        `/organizations/${orgId}/crm-activities/bulk-update`,
        {
          crmActivityIds: (entities as Activity[]).map(
            ({ assignee, crmActivityType, ...rest }) => ({
              ...rest,
              assigneeId: assignee?.id,
              crmActivityType: crmActivityType ? { id: crmActivityType?.id } : undefined,
            })
          ),
        },
        config
      );
    } else {
      invariant(false, `Unsupported entity type in bulk update: ${entityType}`);
    }
  };

  deleteEntities = async (
    orgId: Organization["id"],
    entityType: EntityType,
    entityIds: AnyEntityId[],
    config?: RequestOptions
  ): Promise<BulkDeleteResponse> => {
    const ids = entityIds.map((id) => ({ id })); // <- platform expects id list to have this form
    // without this param platform fails the entire request if at least one entity fails
    const params = { $process: "any" };
    if (entityType === EntityType.COMPANY) {
      return this.delete(`/organizations/${orgId}/accounts/bulk-delete`, {
        data: { accountIds: ids },
        params,
        ...config,
      });
    } else if (entityType === EntityType.PERSON) {
      return this.delete(`/organizations/${orgId}/contacts/bulk-delete`, {
        data: { contactIds: ids },
        params,
        ...config,
      });
    } else if (entityType === EntityType.DEAL) {
      return this.delete(`/organizations/${orgId}/deals/bulk-delete`, {
        data: { dealIds: ids },
        params,
        ...config,
      });
    } else if (entityType === EntityType.ACTIVITY) {
      return this.delete(`/organizations/${orgId}/crm-activities/bulk-delete`, {
        data: { crmActivityIds: ids },
        params,
        ...config,
      });
    } else {
      invariant(false, `Unsupported entity type in bulk delete: ${entityType}`);
    }
  };

  createGroup = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    name: string,
    crmActivityTypeId: ActivityType["id"][],
    color?: AnyColor,
    cadenceInterval?: number,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Group> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.post(
      `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}`,
      { cadenceInterval, color: color || null, crmActivityTypeId, name },
      {
        params: {
          $filters: JSON.stringify($filters),
        },
        ...config,
      }
    );
  };

  updateGroup = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    group: Group,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Group> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.put(
      `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}/${group.id}`,
      group,
      {
        params: {
          $filters: JSON.stringify($filters),
        },
        ...config,
      }
    );
  };

  deleteGroup = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    groupId: Group["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(
      `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}/${groupId}`,
      config
    );

  bulkDeleteGroups = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    groupIds: Group["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}/bulk`, {
      data: {
        groupIds: groupIds.map((id) => ({ id })),
      },
      ...config,
    });

  bulkUpdateGroups = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingGroups,
    groups: Partial<Omit<Group, "id">>[],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(
      `/organizations/${orgId}/${entityTypeToGroupsEndpoint[entityType]}/bulk`,
      groups,
      config
    );

  fetchStatistics = (
    orgId: Organization["id"],
    filters: Partial<{
      date: PlatformSimpleCondition;
      funnel: string;
      summary: "all" | "date" | "funnel" | "user";
      teamId: { $in: Array<Team["id"]> };
      userId: { $in: Array<User["id"]> } | User["id"];
    }>,
    config?: RequestOptions
  ): Promise<ListResponse<Statistics>> =>
    this.get(`/organizations/${orgId}/stats`, {
      params: {
        $filters: filters ? JSON.stringify(filters) : undefined,
      },
      ...config,
    });

  createCompaniesInBulk = async (
    orgId: Organization["id"],
    companies: Array<Partial<CompanyWithLeadId>>,
    config?: RequestOptions
  ): Promise<Company[]> =>
    this.post(`/organizations/${orgId}/accounts/bulk-create`, { accountIds: companies }, config);

  createPeopleInBulk = async (
    orgId: Organization["id"],
    people: Array<Partial<PersonWithLeadId>>,
    config?: RequestOptions
  ): Promise<Person[]> =>
    this.post(`/organizations/${orgId}/contacts/bulk-create`, { contactIds: people }, config);

  createActivitiesInBulk = async (
    orgId: Organization["id"],
    activities: Array<ActivityPayload>,
    config?: RequestOptions
  ): Promise<Activity[]> =>
    this.post(
      `/organizations/${orgId}/crm-activities/bulk-create`,
      { crmActivityIds: activities },
      config
    );

  validateStandardFields = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    entityType: EntityTypesSupportingUniqueStandardFields,
    entity: CreateCompanyPayload | CreateDealPayload | CreatePersonPayload,
    config?: RequestOptions
  ): Promise<FieldValidationErrors[]> => {
    const response: Array<{ errors: ValidationError[] }> = await this.put(
      `/organizations/${orgId}/${entityType}/bulk`,
      [entity],
      { ...config, params: { ...config?.params, layoutId, validateOnly: true } }
    );

    const errorByField: Record<IField["platformName"], ValidationError[]> = groupBy(
      response[0].errors,
      "field"
    );

    return Object.keys(errorByField).map((fieldName) => ({
      error: errorByField[fieldName],
      fieldName,
    }));
  };

  upsertCompaniesInBulk = async (
    orgId: Organization["id"],
    companies: Array<Partial<CompanyWithLeadId>>,
    validateOnly?: boolean,
    config?: RequestOptions
  ): Promise<[successful: Company[], errored: unknown[]]> =>
    (validateOnly ? this.post : this.put).bind(this)(
      `/organizations/${orgId}/accounts/bulk`,
      { accountIds: companies },
      {
        ...config,
        params: {
          ...config?.params,
          validateOnly,
        },
      }
    );

  upsertPeopleInBulk = async (
    orgId: Organization["id"],
    people: Array<Partial<PersonWithLeadId>>,
    config?: RequestOptions
  ): Promise<[successful: Person[], errored: unknown[]]> =>
    this.put(`/organizations/${orgId}/contacts/bulk`, people, config);

  setCompanyParent = (
    orgId: Organization["id"],
    companyId: Company["id"],
    parentId: Company["id"] | null
  ): Promise<Company> =>
    this.put(`/organizations/${orgId}/accounts/${companyId}`, {
      id: companyId,
      parentAccount: { id: parentId },
    });

  signup = (
    token: string,
    fullName: string,
    username: string,
    phone: string,
    password: string,
    organizationName: string,
    marketingEnabled: boolean,
    referralSource: string,
    orgMetaData: Organization["metaData"],
    userMetaData: User["metaData"],
    orgSettings: Record<Setting["key"], { value: Setting["value"] }>,
    config?: RequestOptions
  ): Promise<{ organization: Organization; user: UserRef }> =>
    this.post(
      "/signup",
      {
        client: "Web",
        fullName,
        marketingEnabled,
        metaData: userMetaData,
        organizationName,
        orgMetaData,
        orgSettings,
        password,
        phone,
        referralSource,
        token,
        type: orgMetaData?.numFieldReps === "1" ? "individual" : "team",
        username,
      },
      config
    );

  acceptInvitation = (
    token: string,
    invitationCode: string,
    fullName: string,
    username: string,
    phone: string,
    password: string,
    marketingEnabled: boolean,
    userMetaData: User["metaData"],
    config?: RequestOptions
  ): Promise<{ organization: Organization; user: UserRef }> =>
    this.post(
      "/accept-invitation",
      {
        fullName,
        invitationCode,
        marketingEnabled,
        metaData: userMetaData,
        password,
        phone,
        token,
        username,
      },
      config
    );

  fetchNotes = (
    entityType: EntityTypeSupportingNotes,
    entityId: AnyEntityId,
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<SimpleListResponse<Note>> =>
    this.get(`/${entityType}/${entityId}/notes`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchPlans = (config?: RequestOptions): Promise<ListResponse<Plan>> => this.get(`/plans`, config);

  upgradeSubscription = (
    orgId: Organization["id"],
    token: string,
    planId: Plan["id"],
    licenses: number,
    config?: RequestOptions
  ): Promise<void> =>
    this.post(
      `organizations/${orgId}/upgrade`,
      { cardToken: token, plan: { id: planId }, quantity: licenses },
      config
    );

  startImport = (
    orgId: Organization["id"],
    payload: ImportPayload,
    config?: RequestOptions
  ): Promise<Import> => this.post(`/organizations/${orgId}/imports`, payload, config);

  fetchImport = (
    orgId: Organization["id"],
    importId: Import["id"],
    config?: RequestOptions
  ): Promise<Import> => this.get(`/organizations/${orgId}/imports/${importId}`, config);

  fetchImports = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<Import>> =>
    this.get(`/organizations/${orgId}/imports?$order=-updatedAt`, config);

  createCompany = async (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: CreateCompanyPayload,
    options?: {
      headers?: RequestOptions["headers"];
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Company> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };
    return this.post(`/organizations/${orgId}/accounts`, payload, {
      params: {
        $filters: JSON.stringify($filters),
        layoutId,
      },
      ...config,
    });
  };

  fetchInvitation = (invitationCode: string, config?: RequestOptions): Promise<InvitationInfo> =>
    this.get(`/invitation/${invitationCode}`, config);

  fetchImportConfigs = async (
    orgId: Organization["id"],
    type?: EntityTypesSupportedByImport,
    config?: RequestOptions
  ): Promise<ListResponse<ImportConfig>> => {
    const result = await this.get(`/organizations/${orgId}/import-configs`, {
      params: {
        $filters: JSON.stringify(type ? { type } : {}),
      },
      ...config,
    });
    result.data = result.data.map(parseDatedFields);
    return result;
  };

  createImportConfig = (
    orgId: Organization["id"],
    importConfig: Omit<Partial<ImportConfig>, "id">,
    config?: RequestOptions
  ): Promise<ImportConfig> =>
    this.post(`/organizations/${orgId}/import-configs`, importConfig, config);

  updateImportConfig = (
    orgId: Organization["id"],
    importConfig: ImportConfig,
    config?: RequestOptions
  ): Promise<ImportConfig> =>
    this.put(`/organizations/${orgId}/import-configs/${importConfig.id}`, importConfig, config);

  deleteImportConfig = (
    orgId: Organization["id"],
    importConfigId: ImportConfig["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/import-configs/${importConfigId}`, config);

  // BE returns "" (empty 200 body) when the user has no connection of the given type.
  fetchNylasInfo = (type: NylasType, config?: RequestOptions): Promise<"" | NylasInfo> =>
    this.get("/nylas/me", { params: { $filters: JSON.stringify({ type }) }, ...config });

  createNylas = (payload: NylasInfoCreatePayload, config?: RequestOptions): Promise<NylasInfo> =>
    this.post("/nylas", payload, config);

  updateNylas = (payload: NylasInfoUpdatePayload, config?: RequestOptions): Promise<NylasInfo> =>
    this.put(`/nylas/${payload.id}`, payload, {
      params: { $filters: JSON.stringify({ type: payload.type }) },
      ...config,
    });

  deleteNylas = (nylasId: NylasInfo["id"], config?: RequestOptions): Promise<void> =>
    this.delete(`/nylas/${nylasId}`, config);

  fetchCalendars = (nylasId: NylasInfo["id"], config?: RequestOptions): Promise<void> =>
    this.get(`/nylas/${nylasId}/calendars`, config);

  fetchNylasFolders = (config?: RequestOptions): Promise<NylasFolder[]> =>
    this.get("/nylas/me/folders", config);

  createNylasEvent = (
    nylasId: NylasInfo["id"],
    payload: CreateNylasEventPayload,
    config?: RequestOptions
  ): Promise<unknown> => this.post(`/nylas/${nylasId}/event`, payload, config);

  fetchSavedFilters = async (
    orgId: Organization["id"],
    entityType: EntityType,
    config?: RequestOptions
  ): Promise<SimpleListResponse<PlatformSavedFilter>> =>
    await this.get(`/organizations/${orgId}/saved-filters`, {
      params: {
        $filters: JSON.stringify({ type: entityType }),
        $limit: 2000,
        $order: "-createdAt",
      },
      ...config,
    });

  createSavedFilter = (
    orgId: Organization["id"],
    filter: Omit<PlatformSavedFilter, "createdAt" | "id" | "updatedAt" | "valid">,
    config?: RequestOptions
  ): Promise<PlatformSavedFilter> =>
    this.post(`/organizations/${orgId}/saved-filters`, filter, config);

  updateSavedFilter = (
    orgId: Organization["id"],
    filter: Omit<PlatformSavedFilter, "createdAt" | "updatedAt" | "valid">,
    config?: RequestOptions
  ): Promise<PlatformSavedFilter> =>
    this.put(`/organizations/${orgId}/saved-filters/${filter.id}`, filter, config);

  deleteSavedFilter = (
    orgId: Organization["id"],
    filterId: SavedFilter["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/saved-filters/${filterId}`, config);

  fetchUserSettings = (
    orgId: Organization["id"],
    userId: User["id"],
    config?: RequestOptions
  ): Promise<Setting> => this.get(`/organizations/${orgId}/users/${userId}/settings`, config);

  updateUserSetting = (
    orgId: Organization["id"],
    userId: User["id"],
    setting: Setting,
    config?: RequestOptions
  ): Promise<Setting> =>
    this.put(`/organizations/${orgId}/users/${userId}/settings/${setting.id}`, setting, config);

  fetchPins = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<AggregatedListResponse<MapEntity, ListResponseAggregation<MapEntity>[]>> =>
    this.post(
      `/organizations/${orgId}/pins`,
      {
        $aggs,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters,
        $limit,
        $offset,
        $order,
      },
      config
    );

  updateFile = (
    orgId: Organization["id"],
    file: OnlyRequiredFields<RawFile, "id">,
    config?: RequestOptions
  ): Promise<void> => this.put(`organizations/${orgId}/files/${file.id}`, file, config);

  bulkUpdateFile = (
    orgId: Organization["id"],
    data: OnlyRequiredFields<RawFile, "id">[],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`organizations/${orgId}/files/bulk-update`, { fileIds: data }, config);

  deleteFile = (
    orgId: Organization["id"],
    fileId: RawFile["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`organizations/${orgId}/files/${fileId}`, config);

  createFunnel = (
    orgId: Organization["id"],
    funnel: Partial<Omit<Funnel, "id">>,
    config?: RequestOptions
  ): Promise<Funnel> => {
    return this.post(`organizations/${orgId}/funnels`, funnel, config);
  };

  updateFunnel = (
    orgId: Organization["id"],
    funnel: Funnel,
    config?: RequestOptions
  ): Promise<Funnel> => this.put(`organizations/${orgId}/funnels/${funnel.id}`, funnel, config);

  deleteFunnel = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`organizations/${orgId}/funnels/${funnelId}`, config);

  createStage = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    stage: Partial<Omit<Stage, "id">>,
    config?: RequestOptions
  ): Promise<Stage> => {
    return this.post(`organizations/${orgId}/funnels/${funnelId}/stages`, stage, config);
  };

  updateStage = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    stage: Stage,
    config?: RequestOptions
  ): Promise<Stage> =>
    this.put(`organizations/${orgId}/funnels/${funnelId}/stages/${stage.id}`, stage, config);

  deleteStage = (
    orgId: Organization["id"],
    funnelId: Funnel["id"],
    stageId: Stage["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`organizations/${orgId}/funnels/${funnelId}/stages/${stageId}`, config);

  bulkCreateGroupShare = (
    orgId: Organization["id"],
    sharedGroups: Array<{ groupId: Group["id"]; userId: User["id"] }>,
    config?: RequestOptions
  ): Promise<string> =>
    this.post(
      `organizations/${orgId}/shared-groups/bulk-create`,
      { sharedGroups: sharedGroups },
      config
    );

  bulkCreateRouteShare = (
    orgId: Organization["id"],
    sharedRoutes: Array<{ routeId: Route["id"]; userId: User["id"] }>,
    config?: RequestOptions
  ): Promise<string> =>
    this.post(
      `organizations/${orgId}/shared-groups/bulk-create`,
      {
        sharedGroups: sharedRoutes.map(({ routeId, userId }) => ({ groupId: routeId, userId })),
      },
      config
    );

  bulkDeleteRouteShare = (
    orgId: Organization["id"],
    sharedRoutes: Array<{ routeId: Route["id"]; userId: User["id"] }>,
    config?: RequestOptions
  ): Promise<string> =>
    this.delete(`organizations/${orgId}/shared-groups/bulk-delete`, {
      data: {
        sharedGroups: sharedRoutes.map(({ routeId, userId }) => ({ groupId: routeId, userId })),
      },
      ...config,
    });

  bulkDeleteGroupShare = (
    orgId: Organization["id"],
    sharedGroups: Array<{ groupId: Group["id"]; userId: User["id"] }>,
    config?: RequestOptions
  ): Promise<string> =>
    this.delete(`organizations/${orgId}/shared-groups/bulk-delete`, {
      data: { sharedGroups: sharedGroups },
      ...config,
    });

  getSandboxAccessToken = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<SandboxAccessTokenData> => this.get(`organizations/${orgId}/sandbox-access`, config);

  createTokenHandoffCode = (config?: RequestOptions): Promise<TokenHandoffCodeData> =>
    this.post(`token-handoff/code`, undefined, config);

  createSandbox = (
    orgId: Organization["id"],
    name: string,
    config?: RequestOptions
  ): Promise<OrgCloneRun> =>
    this.post(`organizations/${orgId}/org-clone`, { cloneConfig: { name } }, config);

  updateSandbox = (
    orgId: Organization["id"],
    sandboxConfig: SandboxConfig,
    config?: RequestOptions
  ): Promise<OrgCloneRun> =>
    this.put(`organizations/${orgId}/org-clone`, { cloneConfig: sandboxConfig }, config);

  deleteSandbox = (orgId: Organization["id"], config?: RequestOptions): Promise<void> =>
    this.delete(`organizations/${orgId}/org-clone`, config);

  fetchSandboxes = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<OrgCloneRow>> => this.get(`organizations/${orgId}/org-clone`, config);

  getEntityDuplicates = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingDataMerging,
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<DuplicatePair>> =>
    this.get(`/organizations/${orgId}/${entityTypeToDuplicates[entityType]}`, {
      params: {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  updateDuplicatePair = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingDataMerging,
    duplicate: DuplicatePair,
    config?: RequestOptions
  ): Promise<ListResponse<DuplicatePair>> =>
    this.put(
      `/organizations/${orgId}/${entityTypeToDuplicates[entityType]}/${duplicate.id}`,
      duplicate,
      config
    );

  mergeDuplicates = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingDataMerging,
    duplicateId: DuplicatePair["id"],
    primaryId: DuplicateEntity["id"],
    secondaryId: DuplicateEntity["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.post(
      `/organizations/${orgId}/${mergeEntity[entityType]}`,
      {
        id: duplicateId,
        primary: { id: primaryId },
        secondary: { id: secondaryId },
      },
      config
    );

  getDuplicateRun = (
    orgId: Organization["id"],
    entityType?: EntityTypeSupportingDataMerging,
    config?: RequestOptions
  ): Promise<DuplicateRun> =>
    this.get(
      `/organizations/${orgId}/dupe-run${entityType ? `?entity=${entityType}` : ""}`,
      config
    );

  recalculateDuplicates = (
    orgId: Organization["id"],
    entityType?: EntityTypeSupportingDataMerging,
    config?: RequestOptions
  ): Promise<DuplicateRun> =>
    this.post(
      `/organizations/${orgId}/dupe-run`,
      entityType ? { entity: entityType } : undefined,
      config
    );

  geocodeAddress = (
    orgId: Organization["id"],
    address: Pick<Located, "address" | "city" | "country" | "postalCode" | "region">,
    config?: RequestOptions
  ): Promise<GeocodeResult> => this.post(`organizations/${orgId}/geocode/address`, address, config);

  reverseGeocodeAddress = (
    orgId: Organization["id"],
    point: GeoPoint,
    config?: RequestOptions
  ): Promise<GeocodeResult> => this.post(`organizations/${orgId}/geocode/latlon`, point, config);

  fetchTimelineEvents = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<RawTimelineEvent>> =>
    this.get(`/organizations/${orgId}/timeline`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchNotifications = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<NotificationListResponse<MmcNotification>> =>
    this.get(`/organizations/${orgId}/notifications`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  getNotification = (
    orgId: Organization["id"],
    id: MmcNotification["id"],
    config?: RequestOptions
  ): Promise<MmcNotification> => this.get(`/organizations/${orgId}/notifications/${id}`, config);

  updateNotifications = (
    orgId: Organization["id"],
    ids: MmcNotification["id"][],
    payload: Partial<MmcNotification>,
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<NotificationListResponse<MmcNotification>> =>
    this.put(
      `/organizations/${orgId}/notifications/bulk-update`,
      { id: ids, ...payload },
      {
        params: {
          $filters: $filters ? JSON.stringify($filters) : undefined,
          $limit,
          $offset,
          $order,
        },
        ...config,
      }
    );

  deleteNotifications = (
    orgId: Organization["id"],
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/notifications`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  fetchMapPins = (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformMapRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<MapEntry>> =>
    this.post(
      `/organizations/${orgId}/pins`,
      {
        $columns: $columns ? $columns.join(",") : undefined,
        $filters,
        $limit,
        $offset,
        $order,
      },
      config
    );

  fetchLeads = (
    orgId: Organization["id"],
    { $filters, $limit, $offset }: Partial<PlatformMapRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Lead>> =>
    this.get(`/organizations/${orgId}/google-leads`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
      },
      ...config,
    });

  updateHiddenLeads = (
    orgId: Organization["id"],
    payload: Lead["place_id"][],
    config?: RequestOptions
  ): Promise<ListResponse<Lead["place_id"]>> =>
    this.put(`/organizations/${orgId}/google-leads/blacklist`, payload, {
      ...config,
    });

  deleteHiddenLeads = (
    orgId: Organization["id"],
    payload: Lead["place_id"][],
    config?: RequestOptions
  ): Promise<ListResponse<Lead["place_id"]>> =>
    this.delete(`/organizations/${orgId}/google-leads/blacklist`, {
      ...config,
      data: payload,
    });

  fetchLeadFinderLimits = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<LeadFinderLimit> => this.get(`/organizations/${orgId}/google-leads-quota`, config);

  fetchTerritories = async (
    orgId: Organization["id"],
    { $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Territory>> => {
    const territoryList: ListResponse<Territory> = await this.get(
      `/organizations/${orgId}/territories`,
      {
        params: {
          $columns: $columns ? $columns.join(",") : undefined,
          $filters: $filters ? JSON.stringify($filters) : undefined,
          $limit,
          $offset,
          $order,
        },
        ...config,
      }
    );

    territoryList.data = territoryList.data
      .filter(
        (territory) =>
          (territory.territoryDetail.type === "points" && territory.territoryDetail.points) ||
          territory.territoryDetail.type !== "points" /*&&
            territory.territoryDetail.shapeFile?.publicURI &&
            territory.territoryDetail.shapeFile?.publicURI.endsWith("shape.bjson")*/
      )
      .map(removeExtraCoordinate);

    return territoryList;
  };

  fetchTerritory = async (
    orgId: Organization["id"],
    territoryId: Territory["id"],
    options?: {
      includeAccessStatus?: boolean;
      // For access-check call sites that don't need geometry — skips the
      // shapeFile download + geometryFromBinary parse.
      skipShape?: boolean;
    },
    config?: RequestOptions
  ): Promise<Territory> => {
    const { skipShape, ...filterOptions } = options ?? {};
    const $filters = { includeAccessStatus: true, ...filterOptions };
    let territory: Territory = await this.get(
      `/organizations/${orgId}/territories/${territoryId}`,
      {
        params: {
          $filters: JSON.stringify($filters),
        },
        ...config,
      }
    );

    if (skipShape) {
      return territory;
    }

    if (territory.territoryDetail.type === "points") {
      territory = removeExtraCoordinate(territory);
    } else if (territory.territoryDetail.shapeFile?.publicURI) {
      const response = await fetch(territory.territoryDetail.shapeFile!.publicURI!, {});
      const shapeArrayBuffer = await response.arrayBuffer();
      territory.territoryDetail.shape = geometryFromBinary(shapeArrayBuffer);
    }

    return territory;
  };

  createTerritory = (
    orgId: Organization["id"],
    territory: CreateTerritoryPayload,
    shape?: GeoMultiPolygon | GeoPolygon,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Territory> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };

    const formData = new FormData();
    formData.append("payload", JSON.stringify(territory));
    if (shape) {
      formData.append(
        "shapeFile",
        new Blob([geometryToBinary(shape)], { type: "application/x-binary" }),
        "shape.bjson"
      );
    }

    return this.post(`/organizations/${orgId}/territories`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        $filters: JSON.stringify($filters),
      },
      ...config,
    });
  };

  updateTerritory = (
    orgId: Organization["id"],
    territory: Territory,
    shape?: GeoMultiPolygon | GeoPolygon,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Territory> => {
    const $filters = { includeAccessStatus: true, ...(options ?? {}) };

    let payload = addExtraCoordinate(territory);
    payload = { ...payload, territoryDetail: { ...payload.territoryDetail, shape: undefined } }; // do not send Shape
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    if (shape) {
      formData.append(
        "shapeFile",
        new Blob([geometryToBinary(shape)], { type: "application/x-binary" }),
        "shape.bjson"
      );
    }

    return this.put(`/organizations/${orgId}/territories/${territory.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        $filters: JSON.stringify($filters),
      },
      ...config,
    });
  };

  deleteTerritory = (
    orgId: Organization["id"],
    territoryId: Territory["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/territories/${territoryId}`, config);

  fetchEntityHistory = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingHistory,
    entityId: EntitiesSupportingHistory["id"],
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<EntityHistoryRow>> =>
    this.get(
      `/organizations/${orgId}/${entityTypeToHistoryEndpointEndpoint[entityType]}/${entityId}`,
      {
        params: {
          $filters: $filters ? JSON.stringify($filters) : undefined,
          $limit,
          $offset,
          $order,
        },
        ...config,
      }
    );

  private normalizeBoundaryLegend = (boundaryLegend: BoundaryLegend): BoundaryLegend => {
    // platform sends crmActivities instead of crm-activities for older legends
    // it should not do that anymore, but in order to keep backwards compatibility,
    // we still make this conversion
    return {
      ...boundaryLegend,
      entity: boundaryLegend.entity
        ? (kebabCase(boundaryLegend.entity) as EntityTypesSupportingBoundaryLegends)
        : null,
    };
  };

  fetchBoundaryLegends = async (
    orgId: Organization["id"],
    { $filters, $limit }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<BoundaryLegend>> => {
    const response: ListResponse<BoundaryLegend> = await this.get(
      `/organizations/${orgId}/boundary-legends`,
      {
        params: {
          $filters: $filters ? JSON.stringify($filters) : undefined,
          $limit,
        },
        ...config,
      }
    );
    return { ...response, data: response.data.map(this.normalizeBoundaryLegend) };
  };

  fetchBoundaryStats = (
    orgId: Organization["id"],
    payload: {
      boundaryLegendId: BoundaryLegend["id"];
      bounds?: PlatformBounds;
      level: "fips" | "state" | "territory" | "zip";
    },
    config?: RequestOptions
  ): Promise<BoundaryStatsResponse> => {
    const { boundaryLegendId, bounds, level } = payload;
    return this.get(`/organizations/${orgId}/boundary-stats`, {
      params: {
        $filters: JSON.stringify({
          boundaryLegend: { id: boundaryLegendId, level },
          bounds,
        }),
      },
      ...config,
    });
  };

  createBoundaryLegend = (
    orgId: Organization["id"],
    payload: CreateBoundaryLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<BoundaryLegend> =>
    this.post(`/organizations/${orgId}/boundary-legends`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  updateBoundaryLegend = (
    orgId: Organization["id"],
    payload: UpdateBoundaryLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<BoundaryLegend> =>
    this.put(`/organizations/${orgId}/boundary-legends/${payload.id}`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  deleteBoundaryLegend = (
    orgId: Organization["id"],
    boundaryLegendId: BoundaryLegend["id"],
    config?: RequestOptions
  ): Promise<BoundaryLegend> =>
    this.delete(`/organizations/${orgId}/boundary-legends/${boundaryLegendId}`, config);

  fetchPinLegends = (
    orgId: Organization["id"],
    { $filters, $limit }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<PinLegend>> =>
    this.get(`/organizations/${orgId}/pin-legends`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
      },
      ...config,
    });

  fetchPinLegend = (
    orgId: Organization["id"],
    pinLegendId: PinLegend["id"],
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.get(`/organizations/${orgId}/pin-legends/${pinLegendId}`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  updatePinLegendStats = (
    orgId: Organization["id"],
    pinLegendId: PinLegend["id"],
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend["stats"]> =>
    this.put(`/organizations/${orgId}/pin-legends/${pinLegendId}/stats`, undefined, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  createColorPinLegend = (
    orgId: Organization["id"],
    payload: CreateColorPinLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.post(`/organizations/${orgId}/pin-legends`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  updateColorPinLegend = (
    orgId: Organization["id"],
    payload: UpdateColorPinLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.put(`/organizations/${orgId}/pin-legends/${payload.id}`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  createShapePinLegend = (
    orgId: Organization["id"],
    payload: CreateShapePinLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.post(`/organizations/${orgId}/pin-legends`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  updateShapePinLegend = (
    orgId: Organization["id"],
    payload: UpdateShapePinLegendPayload,
    { $filters }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.put(`/organizations/${orgId}/pin-legends/${payload.id}`, payload, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
      },
      ...config,
    });

  deletePinLegend = (
    orgId: Organization["id"],
    pinLegendId: PinLegend["id"],
    config?: RequestOptions
  ): Promise<PinLegend> =>
    this.delete(`/organizations/${orgId}/pin-legends/${pinLegendId}`, config);

  addUserRecent = (
    orgId: Organization["id"],
    payload: UserRecentPayload,
    config?: RequestOptions
  ): Promise<UserRecent> => this.post(`/organizations/${orgId}/user-activities`, payload, config);

  fetchUserRecents = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<UserRecent>> =>
    this.get(`/organizations/${orgId}/user-activities`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  getLayouts = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<FormLayout>> =>
    this.get(`/organizations/${orgId}/layouts`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  getLayout = (
    orgId: Organization["id"],
    id: FormLayout["id"],
    config?: RequestOptions
  ): Promise<FormLayout> => this.get(`/organizations/${orgId}/layouts/${id}`, config);

  getLayoutRoles = (
    orgId: Organization["id"],
    { $limit, $offset }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<FormLayoutRoleAssociation>> =>
    this.get(`/organizations/${orgId}/layout-roles`, {
      params: {
        $limit,
        $offset,
      },
      ...config,
    });

  updateLayoutRoles = (
    orgId: Organization["id"],
    payload: Array<FormLayoutRoleAssociationRecord>,
    config?: RequestOptions
  ): Promise<void> => this.put(`/organizations/${orgId}/layout-roles/bulk`, payload, config);

  createLayout = (
    orgId: Organization["id"],
    payload: FormLayout,
    config?: RequestOptions
  ): Promise<FormLayout> => this.post(`/organizations/${orgId}/layouts`, payload, config);

  updateLayout = (
    orgId: Organization["id"],
    payload: FormLayout,
    config?: RequestOptions
  ): Promise<FormLayout> =>
    this.put(`/organizations/${orgId}/layouts/${payload.id}`, payload, config);

  deleteLayout = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    config?: RequestOptions
  ): Promise<FormLayout> => this.delete(`/organizations/${orgId}/layouts/${layoutId}`, config);

  createChildLayout = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: ChildLayout,
    config?: RequestOptions
  ): Promise<ChildLayout> =>
    this.post(`/organizations/${orgId}/layouts/${layoutId}/child-layout`, payload, config);

  updateChildLayout = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    payload: ChildLayout,
    config?: RequestOptions
  ): Promise<ChildLayout> =>
    this.put(
      `/organizations/${orgId}/layouts/${layoutId}/child-layout/${payload.id}`,
      payload,
      config
    );

  deleteChildLayout = (
    orgId: Organization["id"],
    layoutId: FormLayout["id"],
    childLayoutId: ChildLayout["id"],
    config?: RequestOptions
  ): Promise<ChildLayout> =>
    this.delete(
      `/organizations/${orgId}/layouts/${layoutId}/child-layout/${childLayoutId}`,
      config
    );

  updateFieldAccess = (
    orgId: Organization["id"],
    payload: FieldAccessUpdate,
    config?: RequestOptions
  ): Promise<SchemaField> =>
    this.put(`/organizations/${orgId}/field-access/${payload.id}`, payload, config);

  bulkUpdateFields = (
    orgId: Organization["id"],
    payload: Array<SchemaField>,
    config?: RequestOptions
  ): Promise<SchemaField> => this.put(`/organizations/${orgId}/field-access`, payload, config);

  fetchSchema = (
    orgId: Organization["id"],
    entityType?: EntityTypesSupportingFieldCustomization,
    config?: RequestOptions
  ): Promise<SchemaField[]> =>
    entityType
      ? this.get(`/organizations/${orgId}/${entityType}/schema`, config)
      : this.get(`/organizations/${orgId}/schemas`, config);

  updateSchema = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportingFieldCustomization,
    schemaField: OnlyRequiredFields<SchemaField, "id">,
    config?: RequestOptions
  ): Promise<SchemaField> =>
    this.put(`/organizations/${orgId}/field-access/${schemaField.id}`, schemaField, config);

  fetchSchemaFieldAccess = (
    orgId: Organization["id"],
    type: EntityTypesSupportingFieldCustomization,
    { $filters, $limit, $offset, $order }: Partial<Omit<PlatformListRequest, "$columns">>,
    config?: RequestOptions
  ): Promise<ListResponse<SchemaField>> =>
    this.get(`/organizations/${orgId}/${type}/schema/field-access`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchDashboards = async (
    orgId: Organization["id"],
    scope?: Scope,
    { $filters, $limit }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Dashboard>> => {
    const response = await this.get(`/organizations/${orgId}/dashboards`, {
      params: {
        $filters: scope
          ? JSON.stringify({
              ...$filters,
              teamId: scope.teamId ?? null,
              userId: scope.userId ?? null,
            })
          : JSON.stringify($filters),
        $limit,
      },
      ...config,
    });

    response.data = response.data.map(convertFromRawDashboard);

    return response;
  };

  fetchDashboard = async (
    orgId: Organization["id"],
    dashboardId: Dashboard["id"],
    config?: RequestOptions
  ): Promise<Dashboard> => {
    const response = await this.get(`/organizations/${orgId}/dashboards/${dashboardId}`, config);
    return convertFromRawDashboard(response);
  };

  updateDashboard = async (
    orgId: Organization["id"],
    dashboard: Dashboard,
    config?: RequestOptions
  ): Promise<Dashboard> => {
    const response = await this.put(
      `/organizations/${orgId}/dashboards/${dashboard.id}`,
      convertToRawDashboard(dashboard),
      config
    );
    return convertFromRawDashboard(response);
  };

  fetchFieldInsightsFocusAreas = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<FiFocusArea[]> =>
    this.get(`/organizations/${orgId}/field-insights/focus-areas`, config);

  fetchFieldInsightsCharts = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<FiCatalogResponse>> =>
    this.get(`/organizations/${orgId}/field-insights/charts`, config);

  fetchAllFieldInsightsCharts = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<FiCatalogResponse>> =>
    this.get(`/organizations/${orgId}/field-insights/charts`, {
      ...config,
      params: { ...config?.params, includeHidden: true },
    });

  updateFieldInsightsChartsVisibility = (
    orgId: Organization["id"],
    charts: FiChartSettings[],
    config?: RequestOptions
  ): Promise<FiChartHiddenState[]> =>
    this.put(`/organizations/${orgId}/field-insights/charts/bulk`, { charts }, config);

  executeFieldInsightsChart = (
    orgId: Organization["id"],
    chartId: FiCatalogResponse["id"],
    payload: FiExecuteRequest,
    config?: RequestOptions
  ): Promise<FiExecuteResponse> =>
    this.post(`/organizations/${orgId}/field-insights/charts/${chartId}/execute`, payload, config);

  summarizeFieldInsightsChart = (
    orgId: Organization["id"],
    chartId: FiCatalogResponse["id"],
    payload: FiSummarizeRequest,
    config?: RequestOptions
  ): Promise<FiSummarizeResponse> =>
    this.post(
      `/organizations/${orgId}/field-insights/charts/${chartId}/summarize`,
      payload,
      config
    );

  fetchFieldInsightsChartSummary = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<FiChartSummary>> =>
    this.get(`/organizations/${orgId}/field-insights/chart-summary`, config);

  updateFieldInsightsChartSummary = (
    orgId: Organization["id"],
    tableId: FiChartSummary["id"],
    payload: FiChartSummaryPayload,
    config?: RequestOptions
  ): Promise<FiChartSummary> =>
    this.put(
      `/organizations/${orgId}/field-insights/chart-summary/${tableId}`,
      { id: tableId, ...payload },
      config
    );

  executeFieldInsightsWordCloud = (
    orgId: Organization["id"],
    payload: FiWordCloudRequest,
    config?: RequestOptions
  ): Promise<FiWordCloudResponse> =>
    this.post(`/organizations/${orgId}/field-insights/word-cloud`, payload, config);

  fetchFieldInsightsWeeklySummary = (
    orgId: Organization["id"],
    payload: FiWeeklySummaryRequest,
    config?: RequestOptions
  ): Promise<FiWeeklySummaryResponse> =>
    this.post(`/organizations/${orgId}/field-insights/weekly-summary`, payload, config);

  fetchFieldInsightsWeeklySummaryLimits = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<FiLimits> =>
    this.get(`/organizations/${orgId}/field-insights/weekly-summary-limits`, config);

  fetchFieldInsightsWeeklySummarySettings = (
    orgId: Organization["id"],
    filter: { teamId?: Team["id"]; userId?: User["id"] },
    config?: RequestOptions
  ): Promise<FiWeeklySummarySettingsListResponse> => {
    const $and: Record<string, unknown>[] = [];
    if (filter.userId != null) {
      $and.push({ userId: filter.userId });
    }
    if (filter.teamId != null) {
      $and.push({ teamId: filter.teamId });
    }
    return this.get(`/organizations/${orgId}/field-insights/weekly-summary-settings`, {
      params: { $filters: JSON.stringify({ $and }) },
      ...config,
    });
  };

  createFieldInsightsWeeklySummarySettings = (
    orgId: Organization["id"],
    payload: FiWeeklySummarySettingsPayload,
    config?: RequestOptions
  ): Promise<FiWeeklySummarySettings> =>
    this.post(`/organizations/${orgId}/field-insights/weekly-summary-settings`, payload, config);

  updateFieldInsightsWeeklySummarySettings = (
    orgId: Organization["id"],
    id: FiWeeklySummarySettings["id"],
    payload: FiWeeklySummarySettingsPayload,
    config?: RequestOptions
  ): Promise<FiWeeklySummarySettings> =>
    this.put(
      `/organizations/${orgId}/field-insights/weekly-summary-settings/${id}`,
      { id, ...payload },
      config
    );

  // The settings a scope generates with, preset included. The list endpoint above only ever
  // returns the caller's own rows, so it cannot answer this.
  fetchFieldInsightsWeeklySummarySettingsInEffect = (
    orgId: Organization["id"],
    filter: { teamId?: Team["id"]; userId?: User["id"] },
    config?: RequestOptions
  ): Promise<FiWeeklySummaryResolvedSettings> =>
    this.get(`/organizations/${orgId}/field-insights/weekly-summary-settings/resolved`, {
      params: filter,
      ...config,
    });

  fetchFieldInsightsWeeklySummaryPresets = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<FiWeeklySummaryPresetListResponse> =>
    this.get(`/organizations/${orgId}/field-insights/weekly-summary-settings/presets`, config);

  // Upsert keyed on (orgId, roleKey): no id to address, and a full replace.
  upsertFieldInsightsWeeklySummaryPreset = (
    orgId: Organization["id"],
    payload: FiWeeklySummaryPresetPayload,
    config?: RequestOptions
  ): Promise<FiWeeklySummaryPreset> =>
    this.put(
      `/organizations/${orgId}/field-insights/weekly-summary-settings/presets`,
      payload,
      config
    );

  deleteFieldInsightsWeeklySummaryPreset = (
    orgId: Organization["id"],
    roleKey: FiPresetRoleKey,
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/field-insights/weekly-summary-settings/presets`, {
      data: { roleKey },
      ...config,
    });

  applyFieldInsightsWeeklySummaryPreset = (
    orgId: Organization["id"],
    roleKey: FiPresetRoleKey,
    mode: FiPresetApplyMode,
    config?: RequestOptions
  ): Promise<FiWeeklySummaryPresetApplyResult> =>
    this.post(
      `/organizations/${orgId}/field-insights/weekly-summary-settings/presets/apply`,
      { mode, roleKey },
      config
    );

  fetchFieldInsightsWordCloudSettings = (
    orgId: Organization["id"],
    filter: { teamId?: Team["id"]; userId?: User["id"] },
    config?: RequestOptions
  ): Promise<FiWordCloudSettingsListResponse> => {
    const $and: Record<string, unknown>[] = [];
    if (filter.userId != null) {
      $and.push({ userId: filter.userId });
    }
    if (filter.teamId != null) {
      $and.push({ teamId: filter.teamId });
    }
    return this.get(`/organizations/${orgId}/field-insights/word-cloud-settings`, {
      params: { $filters: JSON.stringify({ $and }) },
      ...config,
    });
  };

  createFieldInsightsWordCloudSettings = (
    orgId: Organization["id"],
    payload: FiWordCloudSettingsPayload,
    config?: RequestOptions
  ): Promise<FiWordCloudSettings> =>
    this.post(`/organizations/${orgId}/field-insights/word-cloud-settings`, payload, config);

  updateFieldInsightsWordCloudSettings = (
    orgId: Organization["id"],
    id: FiWordCloudSettings["id"],
    payload: FiWordCloudSettingsPayload,
    config?: RequestOptions
  ): Promise<FiWordCloudSettings> =>
    this.put(
      `/organizations/${orgId}/field-insights/word-cloud-settings/${id}`,
      { id, ...payload },
      config
    );

  fetchFieldInsightsWordCloudSettingsInEffect = (
    orgId: Organization["id"],
    filter: { teamId?: Team["id"]; userId?: User["id"] },
    config?: RequestOptions
  ): Promise<FiWordCloudResolvedSettings> =>
    this.get(`/organizations/${orgId}/field-insights/word-cloud-settings/resolved`, {
      params: filter,
      ...config,
    });

  fetchFieldInsightsWordCloudPresets = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<FiWordCloudPresetListResponse> =>
    this.get(`/organizations/${orgId}/field-insights/word-cloud-settings/presets`, config);

  upsertFieldInsightsWordCloudPreset = (
    orgId: Organization["id"],
    payload: FiWordCloudPresetPayload,
    config?: RequestOptions
  ): Promise<FiWordCloudPreset> =>
    this.put(`/organizations/${orgId}/field-insights/word-cloud-settings/presets`, payload, config);

  deleteFieldInsightsWordCloudPreset = (
    orgId: Organization["id"],
    roleKey: FiPresetRoleKey,
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/field-insights/word-cloud-settings/presets`, {
      data: { roleKey },
      ...config,
    });

  applyFieldInsightsWordCloudPreset = (
    orgId: Organization["id"],
    roleKey: FiPresetRoleKey,
    mode: FiPresetApplyMode,
    config?: RequestOptions
  ): Promise<FiPresetApplyResult> =>
    this.post(
      `/organizations/${orgId}/field-insights/word-cloud-settings/presets/apply`,
      { mode, roleKey },
      config
    );

  fetchExecutiveSummarySchedules = (
    orgId: Organization["id"],
    filter: { scope: "team"; teamId: Team["id"] } | { scope: "user"; userId: User["id"] },
    config?: RequestOptions
  ): Promise<ExecutiveSummaryScheduleListResponse> => {
    const $and: Record<string, unknown>[] = [{ scope: filter.scope }];
    if (filter.scope === "user") {
      $and.push({ userId: filter.userId });
    } else {
      $and.push({ teamId: filter.teamId });
    }
    return this.get(`/organizations/${orgId}/executive-summary-schedules`, {
      params: { $filters: JSON.stringify({ $and }) },
      ...config,
    });
  };

  createExecutiveSummarySchedule = (
    orgId: Organization["id"],
    payload: ExecutiveSummaryScheduleCreatePayload,
    config?: RequestOptions
  ): Promise<ExecutiveSummarySchedule> =>
    this.post(`/organizations/${orgId}/executive-summary-schedules`, payload, config);

  updateExecutiveSummarySchedule = (
    orgId: Organization["id"],
    scheduleId: ExecutiveSummarySchedule["id"],
    payload: ExecutiveSummaryScheduleUpdatePayload,
    config?: RequestOptions
  ): Promise<ExecutiveSummarySchedule> =>
    this.put(`/organizations/${orgId}/executive-summary-schedules/${scheduleId}`, payload, config);

  deleteExecutiveSummarySchedule = (
    orgId: Organization["id"],
    scheduleId: ExecutiveSummarySchedule["id"],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/executive-summary-schedules/${scheduleId}`, config);

  fetchUserDetailsSso = (sessionId: string, config?: RequestOptions): Promise<UserDetails> =>
    this.get(`/sso/user/${sessionId}`, config);

  updateUserRequestStatus = (
    payload: { status: UserRequestStatus; username: string },
    config?: RequestOptions
  ): Promise<UserDetails> => this.put(`/sso/user`, payload, config);

  getEmailQuota = (orgId: Organization["id"], config?: RequestOptions): Promise<EmailQuota> =>
    this.get(`/organizations/${orgId}/email-quota`, config);

  cancelEmailLogs = (
    orgId: Organization["id"],
    emailIds: Email["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`/organizations/${orgId}/email-logs/cancel`, { emailLogIds: emailIds }, config);

  updateEmailLog = (
    orgId: Organization["id"],
    emailId: Email["id"],
    payload: OnlyRequiredFields<Email, "id">,
    config?: RequestOptions
  ): Promise<Email> => this.put(`/organizations/${orgId}/email-logs/${emailId}`, payload, config);

  deleteEmailLogs = (
    orgId: Organization["id"],
    payload: {
      emailLogIds?: Email["id"][];
      status?: EmailStatus.CANCELLED | EmailStatus.ERRORED;
    },
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/email-logs/bulk`, { ...config, data: payload });

  cancelEmailLogsByStatus = (
    orgId: Organization["id"],
    status?: EmailStatus,
    config?: RequestOptions
  ): Promise<EmailQuota> =>
    this.put(
      `/organizations/${orgId}/email-logs/cancel`,
      { emailLogIds: [], status: status ? [status] : [] },
      config
    );

  unsubscribeEmail = (code: string, config?: RequestOptions): Promise<void> =>
    this.put(`/unsubscribe`, undefined, {
      ...config,
      params: {
        ...config?.params,
        code,
      },
    });

  fetchEmailUnsubscribeInfo = (
    code: string,
    config?: RequestOptions
  ): Promise<EmailUnsubscribeInfo> =>
    this.get(`/unsubscribe/info`, {
      ...config,
      params: {
        ...config?.params,
        code,
      },
    });

  fetchEmailSyncProgress = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<EmailSyncProgress> => this.get(`/organizations/${orgId}/email-sync/progress`, config);

  fetchEntityEmailThreads = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityId: EntitiesSupportedByEmailFeature["id"],
    {
      $filters,
      $folder,
      $limit,
      $offset,
      $order,
      $search,
    }: Pick<PlatformListRequest, "$filters" | "$limit" | "$offset" | "$order"> & {
      $folder?: EmailFolder;
      $search?: string;
    },
    config?: RequestOptions
  ): Promise<ListResponse<EmailThread>> =>
    this.get(`/organizations/${orgId}/email-logs/${entityType}/${entityId}/threads`, {
      ...config,
      params: {
        ...config?.params,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $folder,
        $limit,
        $offset,
        $order,
        $search: $search || undefined,
      },
    });

  fetchEntityEmailSync = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityId: EntitiesSupportedByEmailFeature["id"],
    config?: RequestOptions
  ): Promise<ListResponse<EntityEmailSync>> =>
    this.get(
      `/organizations/${orgId}/email-sync/${entityTypeToEntityEmailSyncEndpoint[entityType]}/${entityId}`,
      {
        ...config,
      }
    );

  dismissEntityEmailSync = (
    orgId: Organization["id"],
    entityEmailSyncId: EntityEmailSync["id"],
    config?: RequestOptions
  ): void => {
    const payload: EntityEmailSyncDismissPayload = {
      id: entityEmailSyncId,
      isDismissed: true,
    };
    return this.put(`/organizations/${orgId}/email-sync/${entityEmailSyncId}/dismiss`, payload, {
      ...config,
    });
  };

  fetchEntityEmailSubscriptionStatus = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityId: EntitiesSupportedByEmailFeature["id"],
    config?: RequestOptions
  ): Promise<ListResponse<EmailSubscriptionStatus>> =>
    this.get(
      `/organizations/${orgId}/unsubscribe/${entityTypeToEmailUnsubscribeEndpoint[entityType]}/${entityId}/status`,
      {
        ...config,
      }
    );

  subscribeEntityEmail = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityId: EntitiesSupportedByEmailFeature["id"],
    config?: RequestOptions
  ): void =>
    this.delete(
      `/organizations/${orgId}/unsubscribe/${entityTypeToEmailUnsubscribeEndpoint[entityType]}/${entityId}`,
      {
        ...config,
      }
    );

  subscribeEntitiesEmails = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityIds: EntitiesSupportedByEmailFeature["id"][],
    config?: RequestOptions
  ): void =>
    this.delete(
      `/organizations/${orgId}/unsubscribe/${entityTypeToEmailUnsubscribeEndpoint[entityType]}/bulk`,
      {
        ...config,
        data: {
          ids: entityIds,
        },
      }
    );

  unsubscribeEntityEmail = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityId: EntitiesSupportedByEmailFeature["id"],
    config?: RequestOptions
  ): void =>
    this.put(
      `/organizations/${orgId}/unsubscribe/${entityTypeToEmailUnsubscribeEndpoint[entityType]}/${entityId}`,
      {
        ...config,
      }
    );

  unsubscribeEntitiesEmails = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportedByEmailFeature,
    entityIds: EntitiesSupportedByEmailFeature["id"][],
    config?: RequestOptions
  ): void =>
    this.put(
      `/organizations/${orgId}/unsubscribe/${entityTypeToEmailUnsubscribeEndpoint[entityType]}/bulk`,
      {
        ids: entityIds,
      },
      {
        ...config,
      }
    );

  requestEmailFeature = (
    orgId: Organization["id"],
    payload: { featureKey: string },
    config?: RequestOptions
  ): Promise<void> => this.post(`/organizations/${orgId}/feature-request`, payload, config);

  fetchEmails = (
    orgId: Organization["id"],
    { $aggs, $filters, $limit, $offset, $order, $search }: Partial<PlatformListRequest>,
    config?: RequestOptions
  ): Promise<AggregatedListResponse<Email>> =>
    this.get(`/organizations/${orgId}/email-logs`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
        $search,
      },
      ...config,
    });

  fetchEmailTemplates = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<EmailTemplate>> =>
    this.get(`/organizations/${orgId}/email-templates`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchEmailsEntities = (
    orgId: Organization["id"],
    emailId: Email["id"],
    config?: RequestOptions
  ): Promise<ListResponse<EmailEntity>> =>
    this.get(`/organizations/${orgId}/email-logs/${emailId}/entities`, config);

  fetchEmailAttachment = (
    orgId: Organization["id"],
    attachmentId: string,
    messageId: string,
    config?: RequestOptions
  ): void =>
    this.get(`/organizations/${orgId}/emails/attachment-download`, {
      ...config,
      params: { ...config?.params, attachmentId, messageId },
    });

  createEmailTemplate = (
    orgId: Organization["id"],
    payload: CreateEmailTemplatePayload,
    config?: RequestOptions
  ): Promise<EmailTemplate> =>
    this.post(`/organizations/${orgId}/email-templates`, payload, config);

  shareEmailTemplate = (
    orgId: Organization["id"],
    emailTemplateId: EmailTemplate["id"],
    userIds: User["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(
      `/organizations/${orgId}/email-templates/${emailTemplateId}/email-template-assignment`,
      { userIds },
      config
    );

  updateEmailTemplate = (
    orgId: Organization["id"],
    payload: UpdateEmailTemplatePayload,
    config?: RequestOptions
  ): Promise<EmailTemplate> =>
    this.put(`/organizations/${orgId}/email-templates/${payload.id}`, payload, config);

  deleteEmailTemplate = (
    orgId: Organization["id"],
    payload: EmailTemplate["id"],
    config?: RequestOptions
  ): Promise<EmailTemplate> =>
    this.delete(`/organizations/${orgId}/email-templates/${payload}`, config);

  previewEmail = (
    orgId: Organization["id"],
    email: PreviewEmailPayload,
    config?: RequestOptions
  ): Promise<EmailPreview> => this.post(`/organizations/${orgId}/emails/preview`, email, config);

  sendEmail = (
    orgId: Organization["id"],
    email: SendEmailPayload,
    config?: RequestOptions
  ): Promise<Email> =>
    this.post(
      `/organizations/${orgId}/emails?layoutId=${email.layoutId}${
        email.childLayoutId ? `&childLayoutId=${email.childLayoutId}` : ""
      }`,
      email,
      config
    );

  fetchDemographicData = (
    { $filters, $limit, $offset }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<DemographicData, DemographicMetadata>> =>
    this.get(`/demographic-data`, {
      ...config,
      params: {
        ...config?.params,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
      },
    });

  cloneCustomField = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    data: CloneCustomFieldPayload,
    config?: RequestOptions
  ): Promise<CustomField> =>
    this.post(
      `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}/clone`,
      data,
      config
    );

  validateCustomField = (
    orgId: Organization["id"],
    entityType: EntityTypeSupportingCustomFields,
    id: CustomField["id"],
    config?: RequestOptions
  ): Promise<CustomFieldValidateResponse> =>
    this.get(
      `/organizations/${orgId}/${entityTypeToCustomFieldsEndpoint[entityType]}/${id}/validate`,
      config
    );

  restoreRecords = (
    orgId: Organization["id"],
    entityType: EntityTypesSupportingRestoration,
    payload: Array<EntitySupportingRecordRestoration["id"]>,
    config?: RequestOptions
  ): Promise<ChildLayout> =>
    this.put(`/organizations/${orgId}/${entityType}/restore`, payload, config);

  fetchTrips = async (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Trip>> =>
    this.get(`/organizations/${orgId}/trips`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchTrip = async (
    orgId: Organization["id"],
    tripId: Trip["id"],
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Trip> =>
    this.get(`/organizations/${orgId}/trips/${tripId}`, {
      params: {
        $filters: options ? JSON.stringify(options) : undefined,
      },
      ...config,
    });

  createTrip = async (
    orgId: Organization["id"],
    payload: CreateTripPayload,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Trip> => {
    const trip: Trip = await this.post(`/organizations/${orgId}/trips`, payload, config);
    const getOptions = { includeAccessStatus: true, ...(options ?? {}) };
    return this.fetchTrip(orgId, trip.id, getOptions);
  };

  updateTrip = async (
    orgId: Organization["id"],
    payload: UpdateTripPayload,
    options?: {
      includeAccessStatus?: boolean;
    },
    config?: RequestOptions
  ): Promise<Trip> => {
    await this.put(`/organizations/${orgId}/trips/${payload.id}`, payload, config);
    const getOptions = { includeAccessStatus: true, ...(options ?? {}) };
    return this.fetchTrip(orgId, payload.id, getOptions);
  };

  deleteTrips = (
    orgId: Organization["id"],
    tripIds: Trip["id"][],
    deleteTripReason?: string,
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/trips/bulk`, {
      data: { tripIds: tripIds.map((id) => ({ id, deleteTripReason })) },
      ...config,
    });

  fetchGuides = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Guide>> =>
    this.get(`/organizations/${orgId}/user-guides`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchGuidesStatus = (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<GuideUserStatus>> =>
    this.get(`/organizations/${orgId}/user-guides/status`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  updateUserAssignment = (
    orgId: Organization["id"],
    payload: Array<{ guideId: Guide["id"]; userIds: User["id"][] }>,
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`/organizations/${orgId}/user-guides/user-assignment`, payload, config);

  updateGuideProgress = (
    orgId: Organization["id"],
    payload: GuideUserStatus,
    config?: RequestOptions
  ): Promise<void> =>
    this.put(
      `/organizations/${orgId}/user-guides/${payload.guideId}/status/${payload.id}`,
      payload,
      config
    );

  fetchResources = async (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Resource>> =>
    this.get(`/organizations/${orgId}/resources`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchFolders = async (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Folder>> =>
    this.get(`/organizations/${orgId}/folders`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  fetchResource = (
    orgId: Organization["id"],
    id: Resource["id"],
    options?: {
      includeTemplates?: boolean;
      includeTrash?: boolean;
    },
    config?: RequestOptions
  ): Promise<Resource> =>
    this.get(`/organizations/${orgId}/resources/${id}`, {
      params: {
        $filters: JSON.stringify({ includeTrash: true, ...options }),
      },
      ...config,
    });

  bulkCreateResources = async (
    orgId: Organization["id"],
    payload: ResourcesCreatePayload,
    config?: RequestOptions
  ): Promise<ResourceResponse> =>
    this.post(`/organizations/${orgId}/resources/bulk-create`, payload, config);

  createFolder = (
    orgId: Organization["id"],
    data: OnlyRequiredFields<Folder, "name">,
    config?: RequestOptions
  ): Promise<Folder> => this.post(`/organizations/${orgId}/folders`, data, config);

  updateFolder = (
    orgId: Organization["id"],
    payload: OnlyRequiredFields<Folder, "id">,
    config?: RequestOptions
  ): Promise<Folder> => this.put(`/organizations/${orgId}/folders/${payload.id}`, payload, config);

  deleteFolder = (
    orgId: Organization["id"],
    id: Folder["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/folders/${id}`, config);

  updateResource = (
    orgId: Organization["id"],
    payload: ResourcePayload,
    options?: { restore?: boolean; toTrash?: boolean },
    config?: RequestOptions
  ): Promise<Resource> =>
    this.put(`/organizations/${orgId}/resources/${payload.id}`, payload, {
      params: { $restore: options?.restore, $trash: options?.toTrash },
      ...config,
    });

  deleteResource = (
    orgId: Organization["id"],
    id: Resource["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/resources/${id}`, config);

  emptyTrashFolder = (orgId: Organization["id"], config?: RequestOptions): Promise<void> =>
    this.delete(`/organizations/${orgId}/folders/empty-trash`, config);

  bulkEditResources = (
    orgId: Organization["id"],
    data: ResourcePayload[],
    options?: { restore?: boolean; toTrash?: boolean },
    config?: RequestOptions
  ): Promise<void> =>
    this.put(
      `/organizations/${orgId}/resources/bulk-update`,
      { resourceIds: data },
      {
        params: { $restore: options?.restore, $trash: options?.toTrash },
        ...config,
      }
    );

  bulkDeleteResources = (
    orgId: Organization["id"],
    ids: Resource["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/resources/bulk-delete`, {
      data: { resourceIds: ids.map((id) => ({ id })) },
      ...config,
    });

  bulkEditFolders = (
    orgId: Organization["id"],
    data: OnlyRequiredFields<Folder, "id">[],
    config?: RequestOptions
  ): Promise<Folder[]> =>
    this.put(`/organizations/${orgId}/folders/bulk-update`, { folderIds: data }, config);

  bulkDeleteFolders = (
    orgId: Organization["id"],
    ids: Folder["id"][],
    config?: RequestOptions
  ): Promise<Folder["id"][]> =>
    this.delete(`/organizations/${orgId}/folders/bulk-delete`, {
      data: { folderIds: ids.map((id) => ({ id })) },
      ...config,
    });

  fetchStorageConnections = (
    orgId: Organization["id"],
    { $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<StorageConnection>> =>
    this.get(`/organizations/${orgId}/storage-connections`, {
      params: {
        $order,
      },
      ...config,
    });

  fetchStorageConnection = (
    orgId: Organization["id"],
    id: StorageConnection["id"],
    config?: RequestOptions
  ): Promise<StorageConnection> =>
    this.get(`/organizations/${orgId}/storage-connections/${id}`, config);

  createStorageConnection = (
    orgId: Organization["id"],
    data: CreateConnectionPayload,
    config?: RequestOptions
  ): Promise<StorageConnection & { authUrl?: string; state?: string }> =>
    this.post(`/organizations/${orgId}/storage-connections`, data, config);

  updateStorageConnection = (
    orgId: Organization["id"],
    id: StorageConnection["id"],
    data: UpdateConnectionPayload,
    config?: RequestOptions
  ): Promise<StorageConnection> =>
    this.put(`/organizations/${orgId}/storage-connections/${id}`, data, config);

  deleteStorageConnection = (
    orgId: Organization["id"],
    id: StorageConnection["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/storage-connections/${id}`, config);

  browseExternalFolders = (
    orgId: Organization["id"],
    connectionId: StorageConnection["id"],
    params?: { folderId?: string; pageSize?: number; pageToken?: string },
    config?: RequestOptions
  ): Promise<BrowseResponse> =>
    this.get(`/organizations/${orgId}/storage-connections/${connectionId}/browse`, {
      params,
      ...config,
    });

  syncExternalFolders = (
    orgId: Organization["id"],
    connectionId: StorageConnection["id"],
    data: { items: { externalId: string; name: string }[] },
    config?: RequestOptions
  ): Promise<{ itemCount: number; message: string; skippedCount: number }> =>
    this.post(`/organizations/${orgId}/storage-connections/${connectionId}/sync`, data, config);

  triggerManualSync = (
    orgId: Organization["id"],
    connectionId: StorageConnection["id"],
    config?: RequestOptions
  ): Promise<{ message: string }> =>
    this.post(`/organizations/${orgId}/storage-connections/${connectionId}/sync/run`, {}, config);

  fetchFileTags = async (
    orgId: Organization["id"],
    { $aggs, $columns, $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<FileTag>> =>
    this.get(`/organizations/${orgId}/tags`, {
      params: {
        $aggs: $aggs ? JSON.stringify($aggs) : undefined,
        $columns: $columns ? $columns.join(",") : undefined,
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  bulkCreateFileTags = (
    orgId: Organization["id"],
    data: FileTagPayload[],
    config?: RequestOptions
  ): Promise<void> =>
    this.post(`/organizations/${orgId}/tags/bulk-create`, { tagIds: data }, config);

  bulkEditFileTags = (
    orgId: Organization["id"],
    data: OnlyRequiredFields<FileTag, "id">[],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`/organizations/${orgId}/tags/bulk-update`, { tagIds: data }, config);

  bulkDeleteFileTags = (
    orgId: Organization["id"],
    ids: FileTag["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/tags/bulk-delete`, {
      data: { tagIds: ids.map((id) => ({ id })) },
      ...config,
    });

  fetchDrafts = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<Draft>> =>
    this.get(`/organizations/${orgId}/drafts`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  createDraft = (
    orgId: Organization["id"],
    data: CreateDraftPayload,
    config?: RequestOptions
  ): Promise<Draft> => this.post(`/organizations/${orgId}/drafts`, data, config);

  updateDraft = (
    orgId: Organization["id"],
    payload: UpdateDraftPayload,
    config?: RequestOptions
  ): Promise<Draft> => this.put(`/organizations/${orgId}/drafts/${payload.id}`, payload, config);

  deleteDraft = (
    orgId: Organization["id"],
    id: Draft["id"],
    config?: RequestOptions
  ): Promise<void> => this.delete(`/organizations/${orgId}/drafts/${id}`, config);

  fetchStackRankMetrics = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<ListResponse<CustomMetric>> =>
    this.get(`/organizations/${orgId}/stack-rank-metrics`, {
      params: {
        $limit: 1000,
        $order: "name",
      },
      ...config,
    });

  createStackRankMetric = (
    orgId: Organization["id"],
    customMetric: OnlyRequiredFields<CustomMetric, "aggs" | "name" | "tableName">,
    config?: RequestOptions
  ): Promise<CustomMetric> =>
    this.post(`/organizations/${orgId}/stack-rank-metrics`, customMetric, config);

  updateStackRankMetric = (
    orgId: Organization["id"],
    customMetric: CustomMetric,
    config?: RequestOptions
  ): Promise<CustomMetric> =>
    this.put(`/organizations/${orgId}/stack-rank-metrics/${customMetric.id}`, customMetric, config);

  deleteStackRankMetric = (
    orgId: Organization["id"],
    customMetricId: CustomMetric["id"],
    config?: RequestOptions
  ): Promise<CustomMetric> =>
    this.delete(`/organizations/${orgId}/stack-rank-metrics/${customMetricId}`, config);

  fetchUsageLimits = (
    orgId: Organization["id"],
    config?: RequestOptions
  ): Promise<Iam["usageLimits"]> => this.get(`/organizations/${orgId}/usage/limits`, config);

  bulkCreateTerritoryBusinessRules = (
    orgId: Organization["id"],
    payload: BusinessRuleCreatePayload[],
    config?: RequestOptions
  ): Promise<void> =>
    this.post(`/organizations/${orgId}/business-rules/bulk`, { rules: payload }, config);

  bulkUpdateTerritoryBusinessRules = (
    orgId: Organization["id"],
    payload: BusinessRuleUpdatePayload[],
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`/organizations/${orgId}/business-rules/bulk`, { rules: payload }, config);

  bulkDeleteTerritoryBusinessRules = (
    orgId: Organization["id"],
    ruleIds: PlatformBusinessRule["id"][],
    config?: RequestOptions
  ): Promise<void> =>
    this.delete(`/organizations/${orgId}/business-rules/bulk`, {
      data: { ruleIds },
      ...config,
    });

  fetchTerritoryBusinessRules = (
    orgId: Organization["id"],
    { $filters, $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<ListResponse<PlatformBusinessRule>> =>
    this.get(`/organizations/${orgId}/business-rules`, {
      params: {
        $filters: $filters ? JSON.stringify($filters) : undefined,
        $limit,
        $offset,
        $order,
      },
      ...config,
    });

  bulkUpsertTerritoryBusinessRulesAssignments = (
    orgId: Organization["id"],
    payload: BusinessRuleAssignmentPayload[],
    config?: RequestOptions
  ): Promise<void> =>
    this.post(
      `/organizations/${orgId}/business-rules/assignments/bulk-upsert`,
      { assignments: payload },
      config
    );

  generateEmail = (
    orgId: Organization["id"],
    payload: GenerateEmailPayload,
    config?: RequestOptions
  ): Promise<GenerateEmailResponse> =>
    this.post(`/organizations/${orgId}/generate-email`, payload, config);

  submitAiFeedback = (
    orgId: Organization["id"],
    payload: AiFeedbackPayload,
    config?: RequestOptions
  ): Promise<void> => this.post(`/organizations/${orgId}/ai-feedback`, payload, config);

  sendFieldAgentChatMessage = (
    orgId: Organization["id"],
    payload: FieldAgentChatRequest,
    config?: RequestOptions
  ): Promise<FieldAgentChatResponse> =>
    this.post(`/organizations/${orgId}/field-agent/chat`, payload, config);

  transcribeFieldAgentAudio = (
    orgId: Organization["id"],
    audio: Blob,
    config?: RequestOptions
  ): Promise<FieldAgentTranscribeResponse> => {
    const formData = new FormData();
    formData.append("audio", audio, `clip.${audioFileExtension(audio.type)}`);
    return this.post(`/organizations/${orgId}/field-agent/transcribe`, formData, {
      ...config,
      headers: { ...config?.headers, "Content-Type": "multipart/form-data" },
      // Same reasoning uploadFileAsFormData documents: a rep on a truck's uplink must not lose a
      // sentence to the 30s default. Both this and the multipart content type go AFTER the spread,
      // the way uploadFileAsFormData applies them after merging options — ahead of it a caller's
      // config would quietly take the timeout back off, which is the opposite of "no call site can
      // forget it".
      timeout: 0,
    });
  };

  setFieldAgentMessageFeedback = (
    orgId: Organization["id"],
    messageId: number,
    payload: FieldAgentMessageFeedbackPayload,
    config?: RequestOptions
  ): Promise<void> =>
    this.put(`/organizations/${orgId}/ai-feedback/field-agent/${messageId}`, payload, config);

  applyFieldAgentActions = (
    orgId: Organization["id"],
    payload: FieldAgentApplyRequest,
    config?: RequestOptions
  ): Promise<FieldAgentApplyResponse> =>
    this.post(`/organizations/${orgId}/field-agent/actions/apply`, payload, config);

  fetchAnnouncements = (
    { $limit, $offset, $order }: Partial<PlatformListRequest> = {},
    config?: RequestOptions
  ): Promise<SimpleListResponse<Announcement>> =>
    this.get("/announcements", {
      params: { $limit, $offset, $order },
      ...config,
    });

  // The server scopes both announcement calls by the OAuth token's clientType, not by a header.
  markAnnouncementViewed = (
    announcementId: Announcement["id"],
    config?: RequestOptions
  ): Promise<void> => this.post(`/announcements/${announcementId}/views`, undefined, config);
}

export type ApiMethodName = keyof ApiService;
export default ApiService;
